// Wizard de provisioning (SA-2 clients / SA-3 magasins ; SA-4 = kill-switch, cf. Blocks.tsx) — porté
// FIDÈLEMENT de la maquette : modale large, entrée à tuiles (icônes),
// choix du type de base, puis parcours à 2 COLONNES (rail d'étapes numéroté à gauche + carte de contenu),
// et une phase d'exécution. Créer un CLIENT (+ éventuellement son 1er magasin et sa base), ou ajouter un magasin.
// ⚠️ §23.0 : exécution RÉELLE (createTenant/createStore/provisionStore), progression + résultats VRAIS, jamais
// scriptés. Autocomplétion SIRENE = vraie API publique (proxy serveur). Étapes de config sans backend écartées.
import { Children, cloneElement, isValidElement, useEffect, useId, useState, type CSSProperties, type ReactElement, type ReactNode } from 'react';
import { ValidityField, unitWord, type TitleValidity } from '../validity-field';

// Phrases imprimees sur les titres — reprises MOT POUR MOT de Sliderz Retail : c'est le meme texte qui
// sortira de la caisse, il ne doit pas exister en deux formulations.
const TEXTE_AVOIR = (v: number, u: string) => `Avoir valable ${v} ${unitWord(v, u)} à compter de la date d'émission.`;
const TEXTE_CADEAU = (v: number, u: string) => `Chèque cadeau valable ${v} ${unitWord(v, u)} à compter de la date d'émission.`;

import { installableApplications, installableFor } from '@orgazone/applications';
import { api, type SireneResult, type TenantRow, type DeviceRow, type TradeTypeRow, type ReferentialRow } from '../api';
import { DevicePicker } from './device-picker';
import { WizardUpdateGroup, type PickedGroup } from './update-groups';
import { NeptingTerminalFields, neptingEntryProblems } from './store-nepting-modal';
import { companyIdentityProblems, companyVatNumber, normalizeMerchantCode, normalizeNaf, registerIdOf, storeGlnProblem, storeSiretProblem } from '@orgazone/sync-contract';
import { codeCaisse, EmptyState, Icon, rovingRadio, showToast, useOverlayPanel } from '@orgazone/ui-web';
import { fullEanRadical } from '../ui';
import { OFFLINE_RULE_DEFAULTS, offlineRuleError } from '../offline-rule';
import { SYNC_MODES } from '../sync-modes';
import { useAuth, can } from '../auth';
import { AccountWizard } from './account-wizard';
import { qrSvg } from './Fleet';
import { dateTimeLong } from '@orgazone/date-format';
import { localDay, oneYearAfter } from '../rental-days';

type Mode = 'client-mono' | 'client-multi' | 'store';
export interface Preset { mode: 'store'; clientId: string; clientName: string }
/** Pré-remplissage depuis un BROUILLON validé (SA-1) : le mode est choisi par le relecteur, les champs
 *  viennent de l'identité DÉPOSÉE (éventuellement alignée sur Sirene pendant la relecture). */
export interface Prefill { mode: Mode; form: Partial<Form> }

const fieldSt: CSSProperties = { width: '100%', minHeight: 40, padding: '0 13px', border: '1px solid var(--ink200)', borderRadius: 'var(--r-sm)', background: 'var(--canvas)', color: 'var(--ink900)', fontSize: 'var(--t-title)', outline: 'none' };
const lbl: CSSProperties = { display: 'block', fontSize: 'var(--t-eyebrow)', fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--ink500)', marginBottom: 4 };
const card: CSSProperties = { background: 'linear-gradient(180deg,var(--card2),var(--card))', border: '1px solid var(--ink200)', borderRadius: 'var(--r-md)', boxShadow: '0 20px 50px -30px rgba(0,0,0,.5)' };
const eyebrow: CSSProperties = { fontFamily: "'JetBrains Mono',ui-monospace,monospace", fontSize: 'var(--t-micro)', letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--ink500)' };
// ⚠️ LES ICÔNES VIENNENT DU REGISTRE (`@orgazone/ui-web`), jamais d'un dessin local : un jeu de SVG écrit ici divergeait
//    du registre partagé par toutes les applications (règle « un seul registre », cadrage de la Console).
const IC = {
  plus: <Icon name="plus" size={22} />,
  users: <Icon name="users" size={22} />,
  building: <Icon name="building" size={22} />,
  bag: <Icon name="bag" size={22} />,
  grid: <Icon name="grid" size={22} />,
  refresh: <Icon name="refresh" size={22} />,
  tag: <Icon name="tag" size={22} />,
};

interface Form {
  name: string; commercialName: string; siren: string; siretHq: string; naf: string; legalForm: string;
  /** Régime de TVA (décision du 28/10) : franchise en base, et alors un numéro attribué ou non. */
  vatFranchise: boolean; vatNumberAssigned: boolean;
  street: string; cp: string; city: string; contactEmail: string; businessKind: string;
  stName: string; stKind: 'store' | 'depot'; stSiret: string; stGln: string; stStreet: string; stCp: string; stCity: string; syncMode: 'mono' | 'bi'; currency: string;
  offlineRule: boolean; offlineWarnDays: string; offlineLockDays: string;
  // Étape Mobile Partner / Réglages → semés dans la table `setting` de la base à la mise en service.
  mobilePartnerOn: boolean; ticketHeader: string; ticketFooter: string; voucher: TitleValidity; gift: TitleValidity;
  /**
   * LE GROUPE DE MISE À JOUR de chaque application, par nom au store (`@orgazone/applications`) — absent = flux public.
   * ⚠️ Choisi ICI pour que l'appareil le reçoive dès son premier scan : au store, l'opérateur ne fait plus
   *    que télécharger. Il se change ensuite depuis la fiche du magasin.
   */
  updateGroups: Partial<Record<string, PickedGroup>>;
  /**
   * LE CALENDRIER D'EXPLOITATION — fuseau IANA et jours d'ouverture (1 = lundi … 7 = dimanche).
   * ⚠️ 🔴 DEMANDÉS À LA CRÉATION, ET C’EST TOUT L’ENJEU. Les deux ont un repli permissif côté
   * back-office (Europe/Paris, aucun jour fermé) pour qu’un magasin non réglé ne soit JAMAIS bloqué.
   * Ce repli est juste pour un magasin métropolitain ouvert tous les jours, et **silencieusement**
   * faux pour les autres : un magasin ultramarin recevrait ses envois à 4 h du matin, et un magasin
   * fermé le lundi verrait ses rattrapages tomber un jour de fermeture. Un défaut qui ne bloque rien
   * ne se découvre que par la plainte d’un client.
   */
  timezone: string; openDays: number[];
  // ⚠️ Programme de fidélité — MÊMES CLÉS ET MÊMES DÉFAUTS que la tablette (`FID_DEFAULTS`,
  // settings-store.ts). Le wizard ne réinvente rien : il pose ce que Sliderz Retail lira, sinon le
  // magasin naîtrait avec un programme que la caisse ne reconnaît pas.
  fid: FidConf;
}
/** Programme de fidélité tel que la tablette le stocke (clés `fid.*` scalaires + `fid.tiers` JSON). */
interface FidTier { id: string; l: string; color: string; from: string; to: string; rate: string }
interface FidConf {
  enabled: boolean; mode: 'voucher' | 'cashback';
  useFrom: string; conversion: string; welcomeBonus: string;
  tiersOn: boolean; windowDays: string; tiers: FidTier[];
  keepTier: boolean; protectDays: string;
  allowCumul: boolean; printVoucher: boolean; forbidImmediate: boolean;
}
const FID_DEFAUTS: FidConf = {
  enabled: false, mode: 'voucher',
  useFrom: '1.00', conversion: '5.0', welcomeBonus: '5',
  tiersOn: true, windowDays: '365',
  tiers: [
    { id: 'silver', l: 'Silver', color: '#9AA3AB', from: '0', to: '1500', rate: '2.0' },
    { id: 'gold', l: 'Gold', color: '#C79A3A', from: '1501', to: '2500', rate: '3.0' },
    { id: 'plat', l: 'Platinium', color: '#5B6CFF', from: '2501', to: '', rate: '5.0' },
  ],
  keepTier: true, protectDays: '365',
  allowCumul: true, printVoucher: true, forbidImmediate: false,
};
const emptyForm: Form = {
  name: '', commercialName: '', siren: '', siretHq: '', naf: '', legalForm: '', vatFranchise: false, vatNumberAssigned: false, street: '', cp: '', city: '', contactEmail: '', businessKind: '',
  stName: '', stKind: 'store', stSiret: '', stGln: '', stStreet: '', stCp: '', stCity: '', syncMode: 'bi', currency: 'EUR',
  // Règle « synchro périodique obligatoire » (§13.1quater) — défauts 15 / 30 jours (`offline-rule.ts`), PAR COMMERCE, désactivable.
  offlineRule: true, offlineWarnDays: OFFLINE_RULE_DEFAULTS.warnDays, offlineLockDays: OFFLINE_RULE_DEFAULTS.lockDays,
  mobilePartnerOn: false, ticketHeader: '', ticketFooter: '',
  updateGroups: {},
  // ⚠️ LE DÉFAUT EST CELUI DU BACK-OFFICE, pas un autre : deux replis différents pour la même clé
  //    finiraient par diverger, et personne ne saurait lequel s’applique. Les sept jours par défaut,
  //    parce qu’inventer une fermeture SUPPRIMERAIT un envoi que l’exploitant attend.
  timezone: 'Europe/Paris', openDays: [1, 2, 3, 4, 5, 6, 7],
  // ⚠️ Defauts de la TABLETTE (3 mois / 6 mois), pas les 12 mois inventes ici : le magasin doit naitre
  // avec les valeurs que Sliderz Retail applique, sinon le wizard promet une duree que la caisse ignore.
  voucher: { val: 3, unit: 'm', text: TEXTE_AVOIR(3, 'm') },
  gift: { val: 6, unit: 'm', text: TEXTE_CADEAU(6, 'm') },
  fid: FID_DEFAUTS,
};

/**
 * Étape « Fidélité » — MIROIR de l'onglet Fidélité de Sliderz Retail.
 *
 * ⚠️ Les RÈGLES DÉRIVÉES de la tablette sont reproduites, pas seulement les champs : en cashback le
 * seuil d'utilisation n'a pas d'objet, le cumul est forcé et le bon ne s'imprime pas ; avec les
 * paliers actifs, le taux principal n'entre plus dans le calcul. Un wizard qui laisserait saisir ces
 * valeurs ferait croire à un réglage que la caisse ignorera — c'est le même mensonge qu'un champ mort.
 */
function EtapeFidelite({ v, onChange }: { v: FidConf; onChange: (next: FidConf) => void }) {
  const maj = (patch: Partial<FidConf>) => onChange({ ...v, ...patch });
  const updateTier = (id: string, champ: 'from' | 'to' | 'rate', val: string) =>
    maj({ tiers: v.tiers.map((t) => (t.id === id ? { ...t, [champ]: val } : t)) });
  const cashback = v.mode === 'cashback';
  const bloc: CSSProperties = { border: '1px solid var(--ink200)', borderRadius: 'var(--r-md)', padding: 16, marginTop: 12 };
  const heading: CSSProperties = { fontSize: 'var(--t-lead)', fontWeight: 700, marginBottom: 2 };
  const sous: CSSProperties = { fontSize: 'var(--t-note)', color: 'var(--ink500)', lineHeight: 1.5 };
  const line: CSSProperties = { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderTop: '1px solid var(--ink100)' };
  const fige = (texte: string) => <span className="mono" style={{ fontSize: 'var(--t-note)', color: 'var(--ink500)', background: 'var(--ink100)', padding: '3px 9px', borderRadius: 'var(--r-pill)', whiteSpace: 'nowrap' }}>{texte}</span>;
  const petit: CSSProperties = { ...fieldSt, width: 96, minHeight: 34, textAlign: 'right' };

  return (
    <>
      <div style={{ ...eyebrow, marginBottom: 10 }}>programme de fidélité</div>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <div style={heading}>Activer le programme</div>
          <div style={sous}>Cumul des gains et récompenses. Désactivé, le magasin naît sans programme — il pourra en ouvrir un depuis son back-office.</div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {([[false, 'Non'], [true, 'Oui']] as const).map(([val, lab]) => (
            <button key={lab} type="button" onClick={() => maj({ enabled: val })} className={v.enabled === val ? 'seg active' : 'seg'} style={{ height: 34, padding: '0 18px' }}>{lab}</button>
          ))}
        </div>
      </div>

      {v.enabled && (
        <>
          <div style={bloc}>
            <div style={heading}>Mode de restitution</div>
            <div style={sous}>Comment les gains reviennent au client une fois son plafond atteint.</div>
            <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
              {([['voucher', "Bon d'achat", "Impression d'un bon à l'atteinte du plafond. Cumul en option."],
                 ['cashback', 'Cashback', 'Gains déduits directement. Bon non imprimé, cumul permanent.']] as const).map(([m, lab, desc]) => (
                <button key={m} type="button" onClick={() => maj({ mode: m })}
                  className={v.mode === m ? 'row-pick selected' : 'row-pick'}
                  style={{ flex: 1, textAlign: 'left', padding: '12px 14px', color: 'var(--ink900)' }}>
                  <div style={{ fontSize: 'var(--t-body)', fontWeight: 700 }}>{lab}</div>
                  <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink500)', marginTop: 3, lineHeight: 1.45 }}>{desc}</div>
                </button>
              ))}
            </div>
          </div>

          <div style={bloc}>
            <div style={heading}>Réglages des gains</div>
            <div style={sous}>Conversion et seuils du programme.</div>
            <div style={{ ...line, marginTop: 10 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 'var(--t-body)', fontWeight: 600 }}>Utilisation des gains à partir de</div>
                <div style={sous}>{cashback ? 'Sans objet en cashback — les gains sont déduits dès le premier euro.' : 'Seuil minimum pour utiliser les gains.'}</div>
              </div>
              {cashback ? fige('aucun minimum') : <input aria-label="Utilisation des gains à partir de" style={petit} value={v.useFrom} onChange={(e) => maj({ useFrom: e.target.value })} />}
              {!cashback && <span style={sous}>€</span>}
            </div>
            <div style={line}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 'var(--t-body)', fontWeight: 600 }}>Conversion carte fidélité</div>
                <div style={sous}>{v.tiersOn ? 'Défini par palier ci-dessous — le taux principal n’entre pas dans le calcul.' : 'Pourcentage du montant converti en gain.'}</div>
              </div>
              {v.tiersOn ? fige('défini par les paliers') : <><input aria-label="Conversion carte fidélité" style={petit} value={v.conversion} onChange={(e) => maj({ conversion: e.target.value })} /><span style={sous}>%</span></>}
            </div>
            <div style={line}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 'var(--t-body)', fontWeight: 600 }}>Bonus de bienvenue</div>
                <div style={sous}>Gain crédité à la cagnotte dès la création du client.</div>
              </div>
              <input aria-label="Bonus de bienvenue" style={petit} value={v.welcomeBonus} onChange={(e) => maj({ welcomeBonus: e.target.value })} /><span style={sous}>€</span>
            </div>
          </div>

          <div style={bloc}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 240 }}>
                <div style={heading}>Paliers clients</div>
                <div style={sous}>Silver / Gold / Platinium, déclenchés par le cumul d’achats sur une fenêtre glissante.</div>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                {([[false, 'Non'], [true, 'Oui']] as const).map(([val, lab]) => (
                  <button key={lab} type="button" onClick={() => maj({ tiersOn: val })} className={v.tiersOn === val ? 'seg active' : 'seg'} style={{ height: 30, padding: '0 14px' }}>{lab}</button>
                ))}
              </div>
            </div>
            {v.tiersOn ? (
              <>
                <div style={{ ...line, marginTop: 10 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 'var(--t-body)', fontWeight: 600 }}>Fenêtre de calcul</div>
                    <div style={sous}>Montant cumulé sur les N derniers jours glissants.</div>
                  </div>
                  <input aria-label="Fenêtre de calcul" style={petit} value={v.windowDays} onChange={(e) => maj({ windowDays: e.target.value })} /><span style={sous}>jours</span>
                </div>
                <div style={{ marginTop: 12, border: '1px solid var(--ink200)', borderRadius: 'var(--r-sm)', overflow: 'hidden' }}>
                  <div style={{ display: 'flex', gap: 8, padding: '9px 13px', background: 'var(--ink-50)', fontSize: 'var(--t-eyebrow)', fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--ink500)' }}>
                    <span style={{ flex: 1.2 }}>Palier</span><span style={{ flex: 1, textAlign: 'right' }}>De (€)</span><span style={{ flex: 1, textAlign: 'right' }}>À (€)</span><span style={{ flex: 1, textAlign: 'right' }}>Conversion</span>
                  </div>
                  {v.tiers.map((t) => (
                    <div key={t.id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '9px 13px', borderTop: '1px solid var(--ink200)' }}>
                      <span style={{ flex: 1.2, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ width: 10, height: 10, borderRadius: 'var(--r-pill)', background: t.color, flexShrink: 0 }} />
                        <span style={{ fontSize: 'var(--t-body)', fontWeight: 600 }}>{t.l}</span>
                      </span>
                      <input aria-label={`${t.l} — de (€)`} style={{ ...petit, flex: 1, width: 'auto' }} value={t.from} onChange={(e) => updateTier(t.id, 'from', e.target.value)} />
                      {/* Le dernier palier n'a pas de borne haute : « ∞ » le DIT, un champ vide laisserait croire à un oubli. */}
                      <input aria-label={`${t.l} — à (€)`} style={{ ...petit, flex: 1, width: 'auto' }} value={t.to} onChange={(e) => updateTier(t.id, 'to', e.target.value)} placeholder="∞" />
                      <input aria-label={`${t.l} — conversion`} style={{ ...petit, flex: 1, width: 'auto' }} value={t.rate} onChange={(e) => updateTier(t.id, 'rate', e.target.value)} />
                    </div>
                  ))}
                </div>
                <div style={line}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 'var(--t-body)', fontWeight: 600 }}>Protection du statut</div>
                    <div style={sous}>Conserver le palier atteint même si le cumul repasse sous le seuil — évite l’oscillation entre statuts.</div>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {([[false, 'Non'], [true, 'Oui']] as const).map(([val, lab]) => (
                      <button key={lab} type="button" onClick={() => maj({ keepTier: val })} className={v.keepTier === val ? 'seg active' : 'seg'} style={{ height: 30, padding: '0 14px' }}>{lab}</button>
                    ))}
                  </div>
                </div>
                {v.keepTier && (
                  <div style={line}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 'var(--t-body)', fontWeight: 600 }}>Durée de protection</div>
                      <div style={sous}>Durée pendant laquelle le statut reste garanti après un passage de palier.</div>
                    </div>
                    <input aria-label="Durée de protection" style={petit} value={v.protectDays} onChange={(e) => maj({ protectDays: e.target.value })} /><span style={sous}>jours</span>
                  </div>
                )}
              </>
            ) : (
              <div style={{ ...sous, marginTop: 10 }}>Paliers désactivés : le <b>taux de conversion unique</b> ci-dessus s’applique, sans montant minimum ni maximum.</div>
            )}
          </div>

          <div style={bloc}>
            <div style={heading}>Au dépassement du plafond</div>
            <div style={sous}>Ce qui se passe quand le client dépasse le montant d’utilisation des gains en caisse.</div>
            {cashback ? (
              <>
                <div style={{ ...line, marginTop: 10 }}>
                  <div style={{ flex: 1, fontSize: 'var(--t-body)', fontWeight: 600 }}>Cumul des gains</div>{fige('activé par le cashback')}
                </div>
                <div style={line}>
                  <div style={{ flex: 1, fontSize: 'var(--t-body)', fontWeight: 600 }}>Impression du bon d’achat</div>{fige('désactivée par le cashback')}
                </div>
              </>
            ) : ([
              ['allowCumul', 'Autoriser le cumul au-delà du plafond', 'Le client peut continuer à accumuler plutôt qu’imprimer son bon.'],
              ['printVoucher', 'Impression du bon d’achat', 'Imprimer automatiquement le bon à l’atteinte du plafond.'],
              ['forbidImmediate', 'Interdire l’utilisation immédiate', 'Le client doit attendre sa prochaine visite pour dépenser ses gains.'],
            ] as const).map(([clef, lab, desc], idx) => (
              <div key={clef} style={{ ...line, ...(idx === 0 ? { marginTop: 10 } : null) }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 'var(--t-body)', fontWeight: 600 }}>{lab}</div>
                  <div style={sous}>{desc}</div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {([[false, 'Non'], [true, 'Oui']] as const).map(([val, l2]) => (
                    <button key={l2} type="button" onClick={() => maj({ [clef]: val } as Partial<FidConf>)} className={v[clef] === val ? 'seg active' : 'seg'} style={{ height: 30, padding: '0 14px' }}>{l2}</button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );
}

/** Les éléments qu'un `<label>` désigne parmi ceux qu'un `Field` reçoit. */
const LABELABLE = new Set(['input', 'select', 'textarea']);

/**
 * Un champ et son libellé, l'aide dessous.
 * ⚠️ LE LIBELLÉ DÉSIGNE SON CHAMP PAR `htmlFor` : posé à côté sans lien, il ne nommait rien pour une aide technique. Le
 *    champ — le premier `input`, `select` ou `textarea` parmi les enfants — reçoit l'identifiant au rendu : les appelants
 *    n'ont rien à fournir, et la mise en page ne bouge pas (une enveloppe `<label>` aurait pris la règle `.modal label`).
 *    Sans champ (un message d'attente), le libellé ne désigne rien plutôt qu'un identifiant absent. Un GROUPE de boutons
 *    passe par `FieldGroup` : un libellé qui désignerait un bouton ferait cliquer celui-ci dès qu'on clique sur le texte.
 */
function Field({ label, hint, wide, children }: { label: string; hint?: string; wide?: boolean; children: ReactNode }) {
  const id = useId();
  let linked = false;
  const content = Children.map(children, (node) => {
    if (linked || !isValidElement(node) || typeof node.type !== 'string' || !LABELABLE.has(node.type)) return node;
    linked = true;
    return cloneElement(node as ReactElement<{ id?: string }>, { id });
  });
  return <div style={{ gridColumn: wide ? '1 / -1' : undefined }}><label htmlFor={linked ? id : undefined} style={lbl}>{label}</label>{content}{hint && <div style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)', marginTop: 3 }}>{hint}</div>}</div>;
}

/** Un groupe de boutons et son titre — le titre NOMME le groupe, il ne désigne aucun champ. */
function FieldGroup({ label, children }: { label: string; children: ReactNode }) {
  const id = useId();
  return <div><span id={id} className="field-title" style={lbl}>{label}</span><div role="group" aria-labelledby={id}>{children}</div></div>;
}

/**
 * LE CALENDRIER D'EXPLOITATION D'UN MAGASIN — son fuseau, et les jours où il ouvre.
 *
 * ⚠️ 🔴 POURQUOI C’EST DEMANDÉ ICI ET PAS PLUS TARD. Les deux réglages ont un repli permissif côté
 * back-office (Europe/Paris, aucun jour fermé) pour qu’un magasin non réglé ne soit JAMAIS bloqué. Ce
 * repli est juste pour un magasin métropolitain ouvert tous les jours, et **silencieusement faux**
 * pour les autres : un magasin ultramarin recevrait ses SMS à 4 h du matin, et un magasin fermé le
 * lundi verrait ses rattrapages d’envoi tomber un jour de fermeture. **Un défaut qui ne bloque rien ne
 * se découvre que par la plainte d’un client** — le demander à la création est ce qui empêche la flotte
 * de se remplir de magasins mal réglés qu’il faudra ensuite retrouver un par un.
 *
 * ⚠️ LA LISTE DES FUSEAUX VIENT DU NAVIGATEUR (`Intl.supportedValuesOf`), jamais d’une liste écrite à
 * la main : les fuseaux changent plusieurs fois par an, et une liste figée vieillirait en silence. Là
 * où le navigateur ne sait pas répondre, on laisse la saisie libre plutôt que d’imposer un choix faux.
 *
 * ⚠️ « JOUR OUVRÉ » NE VEUT PAS DIRE LUNDI-VENDREDI POUR UN COMMERCE. Le samedi est le meilleur jour
 * d’un SMS de soldes, et beaucoup de magasins ferment le lundi : le sens bancaire aurait
 * systématiquement évité le samedi, l’inverse du besoin.
 */
function CalendrierExploitation(
  { timezone, openDays, onChange }:
  { timezone: string; openDays: number[]; onChange: (v: { timezone: string; openDays: number[] }) => void },
) {
  // ⚠️ `supportedValuesOf` peut manquer (navigateur ancien) : on rend alors une liste VIDE, et le champ
  //    reste une saisie libre. Lever ici priverait de mise en service pour une commodité d’affichage.
  const fuseaux = (() => {
    try {
      const f = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf;
      return typeof f === 'function' ? f('timeZone') : [];
    } catch { return []; }
  })();
  const JOURS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
  /** Le titre du groupe des jours, que le groupe désigne. */
  const daysId = useId();
  const basculer = (j: number) => onChange({
    timezone,
    openDays: openDays.includes(j) ? openDays.filter((x) => x !== j) : [...openDays, j].sort((a, b) => a - b),
  });

  return (
    <div style={{ border: '1px solid var(--ink200)', borderRadius: 10, padding: 14, display: 'grid', gap: 12 }}>
      <div style={{ fontSize: 'var(--t-body)', fontWeight: 700, color: 'var(--ink900)' }}>Calendrier d’exploitation</div>
      <Field label="Fuseau horaire" hint="C’est lui qui fait qu’un envoi programmé à 9 h part à 9 h CHEZ LE MAGASIN. Le serveur, lui, est en UTC.">
        <input className="field-input" list="oz-fuseaux" value={timezone}
          onChange={(e) => onChange({ timezone: e.target.value, openDays })} placeholder="Europe/Paris" />
        <datalist id="oz-fuseaux">{fuseaux.map((z) => <option key={z} value={z} />)}</datalist>
      </Field>
      <div>
        <span id={daysId} className="field-title" style={lbl}>Jours d’ouverture</span>
        <div role="group" aria-labelledby={daysId} style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {JOURS.map((l, k) => {
            const j = k + 1;
            const on = openDays.includes(j);
            return (
              <button key={j} type="button" onClick={() => basculer(j)}
                style={{ height: 30, padding: '0 12px', borderRadius: 999, cursor: 'pointer', fontSize: 'var(--t-sub)', fontWeight: 700,
                  border: `1.5px solid ${on ? 'var(--teal-500)' : 'var(--ink200)'}`,
                  background: on ? 'var(--teal-500)' : 'var(--paper)',
                  color: on ? 'var(--cta-ink)' : 'var(--ink500)' }}>{l}</button>
            );
          })}
        </div>
        <div style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)', marginTop: 5 }}>
          {/* ⚠️ LA PHRASE DIT CE QUE LE SERVEUR FAIT VRAIMENT du jour de fermeture — sans elle, on
              croirait que fermer un jour supprime l’envoi de ce jour-là, alors qu’il est REPORTÉ. */}
          {openDays.length === 0
            ? 'Aucun jour coché : un envoi manqué n’aurait aucun jour où repartir. Cochez au moins un jour.'
            : 'Si le serveur manque un envoi, il repart au prochain jour coché, à l’heure prévue — jamais au milieu de la nuit.'}
        </div>
      </div>
    </div>
  );
}

// Bloc de recherche Sirene encadré (comme la maquette) : titre + input + liste de résultats + confirmation reprise.
/** Les dates d'une location LONGUE retenue à la création : à partir d'aujourd'hui, échéance proposée à un an. */
interface LongDates { from: string; to: string }
// ⚠️ « Aujourd'hui » est le jour LOCAL : lu en UTC, il proposait la veille entre minuit et 2 h à Paris.
const longDatesFromToday = (): LongDates => {
  const from = localDay(new Date());
  return { from, to: oneYearAfter(from) };
};

/**
 * Ce qui ne va pas dans le régime retenu — `null` quand tout est bon. La même règle que le serveur : une location a ses
 * deux dates et l'échéance suit le début ; un achat n'en a pas.
 */
export function regimeProblem(regime: 'longue' | 'achat', dates: LongDates, what: string): string | null {
  if (regime === 'achat') return null;
  if (!dates.from || !dates.to) return `Renseignez le début et l’échéance de la location — ${what}.`;
  if (dates.to <= dates.from) return `L’échéance doit suivre le début du contrat — ${what}.`;
  return null;
}

/** Ce que le récapitulatif et la ligne retenue disent du régime. */
const regimeText = (regime: 'longue' | 'achat', dates: LongDates): string =>
  regime === 'achat' ? 'achat client' : `location longue jusqu’au ${dates.to}`;

/**
 * Le régime d'un appareil retenu à la création, et ses dates — sous la liste, comme dans l'assistant d'équipement.
 * ⚠️ JAMAIS « location courte » ici (règle utilisateur 2026-08-14) : ouvrir un magasin est un engagement de durée ; la
 *    location courte est un prêt ponctuel, posé et rendu depuis « Locations ».
 * ⚠️ LES DATES SONT DEMANDÉES : sans échéance, une location longue n'arme jamais le blocage automatique à échéance + 2 j.
 */
function RegimeRow({ regime, onRegime, dates, onDates }: {
  regime: 'longue' | 'achat'; onRegime: (r: 'longue' | 'achat') => void; dates: LongDates; onDates: (d: LongDates) => void;
}) {
  const dateSt: CSSProperties = { height: 30, padding: '0 9px', borderRadius: 'var(--r-xs)', border: '1px solid var(--ink200)', background: 'var(--canvas)', color: 'var(--ink900)', fontSize: 'var(--t-note)', marginTop: 4, display: 'block' };
  const lblSt: CSSProperties = { fontSize: 'var(--t-meta)', color: 'var(--ink600)', fontWeight: 600 };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '11px 15px', borderTop: '1px solid var(--ink100)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 'var(--t-note)', color: 'var(--ink600)', fontWeight: 600 }}>Régime</span>
        <div className="seg-track">
          {([['longue', 'Location longue'], ['achat', 'Achat client']] as const).map(([k, l]) => (
            <button aria-pressed={regime === k} key={k} type="button" onClick={() => onRegime(k)} className={regime === k ? 'seg active' : 'seg'}>{l}</button>
          ))}
        </div>
        <span style={{ fontSize: 'var(--t-meta)', color: 'var(--ink500)' }}>La location courte se pose depuis « Locations ».</span>
      </div>
      {regime === 'longue' && (
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 14, flexWrap: 'wrap' }}>
          <label style={lblSt}>Début du contrat<input type="date" value={dates.from} onChange={(e) => onDates({ ...dates, from: e.target.value })} style={dateSt} /></label>
          <label style={lblSt}>Échéance<input type="date" value={dates.to} onChange={(e) => onDates({ ...dates, to: e.target.value })} style={dateSt} /></label>
          <span style={{ fontSize: 'var(--t-meta)', color: 'var(--ink500)', flex: '1 1 220px', lineHeight: 1.5 }}>Échéance proposée à un an. Dépassée de 2 jours, l’appareil est bloqué automatiquement.</span>
        </div>
      )}
    </div>
  );
}

/** Le nom d'un appareil retenu, tel que le récapitulatif le cite : son nom au parc, sinon sa série. */
const pickedName = (pool: readonly DeviceRow[], id: string): string => {
  const d = pool.find((x) => x.id === id);
  return d?.name || d?.serial || id.slice(0, 8);
};

function SireneBox({ title, sub, placeholder, kind, onPick }: { title: string; sub: string; placeholder: string; kind: 'societe' | 'etablissement'; onPick: (r: SireneResult) => void }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState<SireneResult[] | null>(null);
  const [picked, setPicked] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const s = q.trim();
    // Une recherche en vol devient caduque : c'est ce chemin, pas elle, qui éteint l'indicateur.
    if (s.length < 3) { setResults(null); setErr(null); setBusy(false); return; }
    setBusy(true); setErr(null);
    // ⚠️ ANNULER LA MINUTERIE NE SUFFIT PAS : une requête déjà partie revient quand même, et ses entreprises — celles
    //    d'une saisie que le champ ne dit plus — s'afficheraient sous la nouvelle, prêtes à être reprises.
    let vivant = true;
    const h = setTimeout(() => {
      api.sireneSearch(s, kind)
        .then((r) => { if (vivant) setResults(r.results); })
        .catch((e) => { if (vivant) setErr((e as Error).message); })
        .finally(() => { if (vivant) setBusy(false); });
    }, 350);
    return () => { clearTimeout(h); vivant = false; };
  }, [q, kind]);
  return (
    <div style={{ padding: '14px 15px', borderRadius: 'var(--r-md)', border: '1px solid var(--ink200)', background: 'var(--ink100)', marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 9, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 'var(--t-body)', fontWeight: 700 }}>{title}</span>
        <span className="mono" style={{ fontSize: 'var(--t-micro)', color: 'var(--ink500)' }}>{sub}</span>
        <div style={{ flex: 1 }} />
        {busy && <span className="mono" style={{ fontSize: 'var(--t-micro)', color: 'var(--in-fg)' }}>recherche…</span>}
      </div>
      <div style={{ position: 'relative' }}>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={placeholder} style={{ ...fieldSt, background: 'var(--card)' }} />
        {results && results.length > 0 && (
          <div style={{ position: 'absolute', top: 44, left: 0, right: 0, zIndex: 20, background: 'var(--card)', border: '1px solid var(--ink200)', borderRadius: 'var(--r-sm)', boxShadow: '0 22px 50px -20px rgba(0,0,0,.6)', overflow: 'hidden', maxHeight: 296, overflowY: 'auto' }}>
            {results.map((r, i) => (
              <button key={i} type="button" onClick={() => { onPick(r); setPicked(`${r.name} · ${kind === 'etablissement' ? `SIRET ${r.siret ?? '—'}${r.siege ? ' (siège)' : ''}` : `SIREN ${r.siren ?? '—'}`} · ${r.city ?? ''} — repris de Sirene`); setQ(''); setResults(null); }}
                style={{ display: 'block', width: '100%', padding: '10px 13px', border: 0, borderBottom: i ? '1px solid var(--ink100)' : 0, background: 'transparent', cursor: 'pointer', color: 'var(--ink900)', textAlign: 'left' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 'var(--t-lead)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</span>
                  {r.closed && <span style={{ fontSize: 'var(--t-micro)', color: 'var(--da-fg)' }}>{kind === 'etablissement' ? 'fermé' : 'cessée'}</span>}
                  {/* ⚠️ Le siège parmi les établissements : son adresse n'est celle d'un magasin que s'il y vend. */}
                  {kind === 'etablissement' && r.siege && <span data-sirene-siege style={{ fontSize: 'var(--t-micro)', color: 'var(--in-fg)' }}>siège</span>}
                  <span style={{ flex: 1 }} />
                  <span className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)', flexShrink: 0 }}>{r.siren}</span>
                </span>
                <span style={{ display: 'block', fontSize: 'var(--t-meta)', color: 'var(--ink500)', marginTop: 3 }}>{[r.naf, [r.cp, r.city].filter(Boolean).join(' ')].filter(Boolean).join(' · ')}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      {err && <div className="note" style={{ marginTop: 8 }}>{err} — la saisie manuelle reste possible, aucun champ n'est bloqué.</div>}
      {picked && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginTop: 10, padding: '9px 12px', borderRadius: 'var(--r-sm)', border: '1px solid rgba(45,212,191,.35)', background: 'var(--ok-bg)' }}>
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--ok-fg)', flexShrink: 0 }} />
          <span style={{ flex: 1, minWidth: 0, fontSize: 'var(--t-note)', color: 'var(--ok-fg)' }}>{picked}</span>
          <button onClick={() => setPicked(null)} className="btn-ghost" style={{ height: 26, padding: '0 10px', borderRadius: 'var(--r-xs)', fontSize: 'var(--t-meta)', fontWeight: 600 }}>Vider</button>
        </div>
      )}
    </div>
  );
}

/**
 * La fin d'une mise en service : le compte administrateur du client, ouvert sur le magasin qu'on vient de créer.
 * ⚠️ Il s'ouvre sur un magasin RÉEL : sans magasin créé (client multi-sites), l'assistant n'aurait rien à ouvrir —
 *    l'écran dit alors où le faire ensuite. Une fois le compte créé, l'offre cède la place à ce qui a été fait.
 */
export function AdminAccountOffer({ openedStore, contactEmail }: { openedStore: { tenantId: string; tenantName: string; gln: string } | null; contactEmail: string }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  if (result) return <div data-admin-offer className="note" style={{ marginTop: 12 }}>{result}</div>;
  if (!openedStore || !can(user, 'console.account.write')) {
    return <div data-admin-offer className="note" style={{ marginTop: 12 }}>Compte administrateur du client : depuis sa fiche, onglet Utilisateurs{openedStore ? '' : ', une fois le premier magasin ouvert'}.</div>;
  }
  return (
    <div data-admin-offer style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
      <span style={{ flex: 1, minWidth: 200, fontSize: 'var(--t-note)', color: 'var(--ink600)' }}>Compte administrateur du client : son gérant n’a pas encore d’accès au back-office.</span>
      <button type="button" className="btn btn-cta sm" onClick={() => setOpen(true)}>Créer le compte administrateur…</button>
      {open && (
        <AccountWizard
          preset={{ ...openedStore, glns: [openedStore.gln], contactEmail: contactEmail.trim() || undefined, boRole: 'admin' }}
          onClose={() => setOpen(false)}
          onDone={(n) => { setOpen(false); setResult(n); showToast(n); }}
        />
      )}
    </div>
  );
}

export function ProvisioningWizard({ onClose, onDone, preset, prefill }: { onClose: () => void; onDone: (createdClientId?: string) => void; preset?: Preset; prefill?: Prefill }) {
  const [phase, setPhase] = useState<'entry' | 'type' | 'steps' | 'run'>(preset || prefill ? 'steps' : 'entry');
  const [mode, setMode] = useState<Mode | null>(prefill?.mode ?? preset?.mode ?? null);
  const [step, setStep] = useState(0);
  /** Le préfixe des identifiants qui relient un libellé à son champ, ou un titre à son groupe. */
  const uid = useId();
  // ⚠️ ÉCHAP FERME, ET LE FOCUS ENTRE PUIS REVIENT AU DÉCLENCHEUR : au seul pointeur, la modale ne se quittait pas au clavier.
  // ⚠️ L'ASSISTANT DE COMPTE, OUVERT PAR-DESSUS EN FIN DE PARCOURS, EST UNE COUCHE DE LA MÊME PILE : Échap lui revient
  //    tant qu'il est ouvert, et ne ferme pas toute la mise en service.
  const { panelRef } = useOverlayPanel({ open: true, onClose });
  const [f, setF] = useState<Form>(prefill ? { ...emptyForm, ...prefill.form } : emptyForm);
  const set = (patch: Partial<Form>) => setF((prev) => ({ ...prev, ...patch }));
  const [clientId, setClientId] = useState<string | null>(preset?.clientId ?? null);
  const [clientName, setClientName] = useState(preset?.clientName ?? '');
  const [tenants, setTenants] = useState<TenantRow[] | null>(null);
  const [pcQ, setPcQ] = useState('');
  const [err, setErr] = useState<string | null>(null);
  // ⚠️ `lines` : le RAPPORT du serveur, une ligne par fait etabli — une seule cellule `detail` ne
  // pouvait porter que le premier. `warn` : ce qui a echoue SANS annuler la creation (declaration BO ou
  // EDI, best-effort cote serveur) — c'est precisement ce qui etait jete jusqu'ici, donc invisible.
  const [runSteps, setRunSteps] = useState<{ label: string; status: 'pending' | 'run' | 'ok' | 'error'; detail?: string; lines?: string[]; warn?: string[] }[]>([]);
  const [runError, setRunError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [createdClientId, setCreatedClientId] = useState<string | null>(null);
  // Le magasin réellement créé — la fin de parcours y propose le compte administrateur du client.
  const [openedStore, setOpenedStore] = useState<{ tenantId: string; tenantName: string; gln: string } | null>(null);
  // Pool d'Mobile Partner DISPONIBLES au parc — l'affectation ne se propose que parmi du matériel réel (§23.0).
  const [osPool, setOsPool] = useState<DeviceRow[]>([]);
  /**
   * PREMIÈRE CAISSE du magasin — facultative, et volontairement au SINGULIER (contrairement aux
   * Mobile Partner) : un magasin ouvre avec une caisse, les suivantes s'ajoutent depuis sa fiche. Poser ici
   * un parc entier ferait de ce wizard le lieu de gestion du parc, ce qu'il n'est pas.
   *
   * ⚠️ POURQUOI ICI : le jeton d'appairage s'émet PAR APPAREIL — sans caisse affectée, il n'y a rien
   * à appairer et aucun QR ne peut exister. C'est donc l'affectation, et non le jeton, qui était
   * l'étape manquante du parcours d'ouverture (décision utilisateur 2026-08-14).
   */
  const [caissePool, setCaissePool] = useState<DeviceRow[]>([]);
  const [caissePick, setCaissePick] = useState<string | null>(null);
  // Même règle que l'Mobile Partner : équiper un magasin qu'on ouvre est un engagement de durée.
  const [caisseRegime, setCaisseRegime] = useState<'longue' | 'achat'>('longue');
  const [caisseDates, setCaisseDates] = useState<LongDates>(longDatesFromToday);
  // Jeton émis à la fin de l'exécution — porte l'appareil, jamais le magasin.
  const [caisseJeton, setCaisseJeton] = useState<{ token: string; url: string; expiresAt: string; registerNumber: number | null } | null>(null);
  /**
   * LE CODE COMMERÇANT NEPTING DE CETTE PREMIÈRE CAISSE — facultatif, posé dès que l'affectation lui a attribué son n°.
   * ⚠️ PAR CAISSE, DONC ICI ET PAS DANS UNE ÉTAPE « PAIEMENT CARTE » DU MAGASIN : des magasins encaissent sur plusieurs TPE
   *    à contrats différents. Les caisses suivantes reçoivent le leur depuis la fiche du magasin.
   */
  const [caisseNepting, setCaisseNepting] = useState({ code: '', url: '' });
  const [osErr, setOsErr] = useState<string | null>(null);
  // ⚠️ Les types de commerce viennent des RÉFÉRENTIELS (jamais d'une liste en dur) : c'est le même
  // assemblage qui sera injecté dans la base du magasin à sa mise en service (SA-10.1/SA-10.2).
  const [trades, setTrades] = useState<TradeTypeRow[] | null>(null);
  const [tradesErr, setTradesErr] = useState<string | null>(null);
  const [refRows, setRefRows] = useState<ReferentialRow[]>([]);
  // ⚠️ PLUSIEURS terminaux (maquette : `osPick` est une LISTE). Un magasin peut ouvrir avec deux
  // scanners — l'inventaire se fait à deux —, et rien côté parc ne l'interdisait : c'est l'écran qui
  // bridait à un seul.
  const [osPick, setOsPick] = useState<string[]>([]);
  /**
   * Régime d'affectation d'un Mobile Partner à la MISE EN SERVICE.
   *
   * ⚠️ JAMAIS « location courte » ici (règle utilisateur 2026-08-14) : équiper un magasin qu'on ouvre,
   * c'est un engagement de durée — location longue ou achat. La location COURTE est un prêt ponctuel
   * (dépannage, saison, inventaire) : elle se pose et se rend depuis l'écran **Locations**, avec ses
   * dates et son retour, et n'a rien à faire dans un parcours de création.
   */
  const [osRegime, setOsRegime] = useState<'longue' | 'achat'>('longue');
  const [osDates, setOsDates] = useState<LongDates>(longDatesFromToday);
  // Adresse du SIÈGE du client rattaché (mode « ajout magasin ») — sert au bouton « Reprendre le siège ».
  // En mono-client le siège est dans le formulaire (f.street…) ; ici on va la CHERCHER pour un client existant.
  const [hqInfo, setHqInfo] = useState<{ street: string; cp: string; city: string; siret: string; siren: string } | null>(null);
  // Compteurs PRÉVUS pour le futur magasin, calculés sur l'annuaire RÉEL avec la règle du serveur
  // (n° magasin = max du client + 1 ; radical EAN = max GLOBAL + 1, sur 5 chiffres).
  // ⚠️ C'est une PRÉVISION, pas une réservation : le serveur numérote au moment de l'insertion, et il
  // le fait en JS sans séquence SQL. Une création concurrente peut donc la démentir — d'où le libellé
  // « prévu » à l'écran ET la comparaison au retour de `createStore`, qui AVERTIT si ça a bougé.
  /**
   * LES DEVISES OFFERTES — lues au référentiel, jamais écrites dans cet écran.
   * ⚠️ `null` = pas encore lues ; `[]` = référentiel vide. On DISTINGUE les deux à l'écran : « en
   * cours de lecture » et « aucune devise déclarée » ne se répondent pas de la même façon.
   */
  const [devises, setDevises] = useState<{ code: string; label: string; fraction: number }[] | null>(null);
  useEffect(() => {
    let vivant = true;
    api.referentials()
      .then((r) => {
        if (!vivant) return;
        setDevises(r.referentials
          .filter((x) => x.list === 'devises')
          .map((x) => ({ code: x.code, label: x.label, fraction: Number(x.attrs?.fraction ?? 2) })));
      })
      // ⚠️ Un échec de lecture laisse la liste VIDE, et l'écran le dit : le magasin partira en euros,
      // ce qui est le cas de la quasi-totalité — mais on ne fait pas semblant d'avoir proposé un choix.
      .catch(() => { if (vivant) setDevises([]); });
    return () => { vivant = false; };
  }, []);
  const [prevu, setPrevu] = useState<{ sCode: string; radicalEan: string } | null>(null);
  const [prevuErr, setPrevuErr] = useState<string | null>(null);
  // Écart constaté à la création réelle : le compteur avait avancé entre la prévision et l'insertion.
  const [ecartCompteur, setEcartCompteur] = useState<string | null>(null);

  // ⚠️ Les échecs de ces deux sources sont CONSERVÉS : sans eux, l'étape « client » restait muette et le
  // parc Mobile Partner affichait « aucun terminal disponible » — une conclusion inventée à partir d'une
  // requête ratée (§23.0).
  const [sourceErr, setSourceErr] = useState<string | null>(null);
  // ⚠️ UNE RÉPONSE DÉPASSÉE N'ÉCRIT PAS : changer de parcours pendant la lecture puis revenir en relance une seconde —
  //    la première, revenue après, réécrirait la liste ou poserait « Clients non chargés » sur une étape qui a chargé.
  useEffect(() => {
    if (mode !== 'store' || clientId) return;
    let alive = true;
    api.tenants()
      .then((r) => { if (!alive) return; setTenants(r.tenants); setSourceErr(null); })
      .catch((e) => { if (alive) setSourceErr(`Clients non chargés : ${(e as Error).message}`); });
    return () => { alive = false; };
  }, [mode, clientId]);
  useEffect(() => {
    if (mode === 'client-mono' || !clientId) { setHqInfo(null); return; }
    let alive = true;
    api.tenant(clientId).then((d) => { if (!alive) return; const a = d.tenant.address; setHqInfo({ street: a?.voie ?? '', cp: a?.cp ?? '', city: a?.ville ?? '', siret: d.tenant.siretHq ?? '', siren: d.tenant.siren ?? '' }); }).catch(() => { if (alive) setHqInfo(null); });
    return () => { alive = false; };
  }, [mode, clientId]);
  // Prévision des compteurs : une seule lecture de l'annuaire suffit aux deux (le radical est global).
  // ⚠️ Rejouée à chaque changement de client rattaché — la racine dépend DU client, pas du wizard.

  const creeUnMagasin = mode === 'client-mono' || mode === 'store';
  useEffect(() => {
    if (!creeUnMagasin) { setPrevu(null); return; }
    let alive = true;
    api.stores()
      .then((r) => {
        if (!alive) return;
        const num = (v: string | null): number => parseInt(v ?? '0', 10) || 0;
        const duClient = clientId ? r.stores.filter((s) => s.tenantId === clientId) : [];
        const maxS = duClient.reduce((m, s) => Math.max(m, num(s.sCode)), 0);
        const maxRadical = r.stores.reduce((m, s) => Math.max(m, num(s.radicalEan)), 0);
        setPrevu({ sCode: String(maxS + 1), radicalEan: String(maxRadical + 1).padStart(5, '0') });
        setPrevuErr(null);
      })
      .catch((e) => { if (alive) { setPrevu(null); setPrevuErr((e as Error).message); } });
    return () => { alive = false; };
  }, [creeUnMagasin, clientId]);

  // Types de commerce + listes maîtres : servent à proposer le choix ET à en montrer la conséquence
  // (nombre de lignes réellement injectées). Deux sources, un seul échec affiché.
  useEffect(() => {
    Promise.all([api.referentialTrades(), api.referentials()])
      .then(([t, r]) => { setTrades(t.trades); setRefRows(r.referentials); setTradesErr(null); })
      .catch((e) => setTradesErr((e as Error).message));
  }, []);
  // Charge les Mobile Partner disponibles à l'ouverture d'un parcours qui crée un magasin (mono-client ou magasin seul).
  // ⚠️ UNE RÉPONSE DÉPASSÉE N'ÉCRIT PAS : deux changements de parcours lancent deux lectures du parc — la plus
  //    ancienne, revenue la dernière, reproposerait un appareil affecté entre-temps comme disponible.
  useEffect(() => {
    if (mode === 'client-multi') return;
    let alive = true;
    api.devices().then((r) => {
      if (!alive) return;
      const dispo = r.devices.filter((x) => x.status === 'available');
      // ⚠️ Rangés par la NATURE de leur type (référentiel « Types d'équipement »), jamais par le libellé : tous les
      //    types de nature caisse sont proposés ensemble, et un appareil sans type ne l'est nulle part.
      setOsPool(dispo.filter((x) => x.nature === 'mobile-partner'));
      setCaissePool(dispo.filter((x) => x.nature === 'caisse'));
      setOsErr(null);
    })
      .catch((e) => { if (alive) setOsErr((e as Error).message); });
    return () => { alive = false; };
  }, [mode]);

  // Comptage RÉEL des lignes d'une liste (une grille compte ses valeurs, pas « 1 ») — sert à annoncer
  // ce que la mise en service écrira vraiment dans la base.
  const linesOfList = (key: string): number => {
    if (key.startsWith('size_grids:')) {
      const g = refRows.find((r) => r.list === 'size_grids' && r.code === key.split(':')[1]);
      const v = g?.attrs?.values;
      return Array.isArray(v) ? v.length : 0;
    }
    return refRows.filter((r) => r.list === key).length;
  };
  const listLabel = (key: string): string => {
    if (key.startsWith('size_grids:')) {
      return refRows.find((r) => r.list === 'size_grids' && r.code === key.split(':')[1])?.label ?? key;
    }
    return key.replace(/_/g, ' ');
  };
  const linesOfTrade = (t: TradeTypeRow): number => t.lists.reduce((a, k) => a + linesOfList(k), 0);
  const picked = trades?.find((t) => t.label === f.businessKind) ?? null;

  // ⚠️ La FIDÉLITÉ a son étape (2026-08-13) : elle tenait dans un champ texte libre dont seul « vide
  // ou non » était conservé — une saisie qui promettait plus qu'elle ne faisait. Le programme compte
  // treize réglages côté tablette ; ils ne rentrent pas dans une ligne de formulaire.
  // ⚠️ LA SYNCHRO PÉRIODIQUE A SON ÉTAPE (maquette du 28/10) : la même règle que la fiche magasin, Réglages.
  const STORE_STEPS = [{ k: 'store', t: 'Le magasin' }, { k: 'sync', t: 'Synchronisation' }, { k: 'periodic', t: 'Synchro périodique' }, { k: 'caisse', t: 'Première caisse' }, { k: 'mobile-partner', t: 'Mobile Partner' }, { k: 'reglages', t: 'Réglages du magasin' }, { k: 'fidelite', t: 'Fidélité' }];
  /** Étapes d'un parcours. Extrait pour que le RAIL d'étapes et le compteur « N étapes » de l'écran
   *  de choix sortent de la même source : annoncer 7 étapes puis en dérouler 8 serait un mensonge
   *  d'affichage, et c'est le genre d'écart qui survit longtemps parce qu'il paraît anodin. */
  const stepsFor = (m: typeof mode, avecClient: boolean): { k: string; t: string }[] => (m === 'client-multi'
    ? [{ k: 'ident', t: 'Identité du client' }, { k: 'metier', t: 'Type de commerce' }, { k: 'recap', t: 'Récapitulatif' }]
    : m === 'client-mono'
      ? [{ k: 'ident', t: 'Identité du client' }, { k: 'metier', t: 'Type de commerce' }, ...STORE_STEPS, { k: 'recap', t: 'Récapitulatif' }]
      : [...(avecClient ? [] : [{ k: 'pick', t: 'Client de rattachement' }]), ...STORE_STEPS, { k: 'recap', t: 'Récapitulatif' }]);
  const stepList = stepsFor(mode, !!clientId);
  const cur = stepList[step]?.k;
  // Siège à reprendre : en mono-client il vient du formulaire ; en ajout-magasin, de la fiche client chargée.
  const hq = mode === 'client-mono' ? { street: f.street, cp: f.cp, city: f.city, siret: f.siretHq, siren: f.siren } : hqInfo;
  const hqAddr = hq ? [hq.street, hq.cp, hq.city].filter(Boolean).join(' ') : '';
  // Pré-remplit l'adresse (et le SIRET/nom si vides) du magasin depuis le siège — pratique quand le magasin EST au siège.
  const useHq = () => { if (!hq) return; set({ stStreet: hq.street, stCp: hq.cp, stCity: hq.city, stSiret: f.stSiret || hq.siret, stName: f.stName || clientName || f.name }); };

  /**
   * Ce qui manque à une étape — vide quand elle est complète. ⚠️ UNE RÈGLE pour « Continuer » et pour le récapitulatif :
   * la liste d'étapes permet de sauter en avant, donc le récapitulatif rassemble les manques de TOUTES les étapes et
   * verrouille le lancement tant qu'il en reste.
   */
  const problemsOf = (k: string): string[] => {
    const out: (string | false)[] = [];
    // La règle du serveur, à la création comme à la modification d'un client (`companyIdentityProblems`).
    if (k === 'ident') out.push(...Object.values(companyIdentityProblems({ name: f.name, siren: f.siren, siretHq: f.siretHq, naf: f.naf, zip: f.cp, email: f.contactEmail })));
    if (k === 'pick') out.push(!clientId && 'Choisissez le client de rattachement.');
    // La règle du serveur à la déclaration d'un magasin (`storeGlnProblem`), clé de contrôle comprise.
    if (k === 'store') {
      out.push(!f.stName.trim() && 'Le nom du magasin est obligatoire.', storeGlnProblem(f.stGln) ?? false);
      // La règle du serveur (`storeSiretProblem`) : le SIRET descend en `company.siret`, sous la raison sociale du client.
      out.push(storeSiretProblem(f.stSiret, hq?.siren) ?? false);
    }
    if (k === 'reglages') {
      // ⚠️ 🔴 LE CALENDRIER EST OBLIGATOIRE (décision utilisateur 2026-09) : sans fuseau, le
      //    back-office REFUSE les campagnes de ce magasin. Le demander ici est ce qui rend ce refus
      //    anecdotique — un magasin neuf ne le rencontre jamais.
      out.push(!f.timezone.trim() && 'Le fuseau horaire du magasin est obligatoire — il décide de l’heure à laquelle partent les messages.');
      // ⚠️ AU MOINS UN JOUR : avec zéro jour ouvert, un envoi manqué ne trouve aucune date où repartir
      //    et ne part jamais, en silence.
      out.push(f.openDays.length === 0 && 'Cochez au moins un jour d’ouverture — c’est là qu’un envoi manqué repart.');
    }
    if (k === 'periodic') out.push(offlineRuleError(f.offlineRule, f.offlineWarnDays, f.offlineLockDays) ?? false);
    // ⚠️ « OUI » SANS TERMINAL EST UN MANQUE (décision du 28/10, la maquette) : un magasin équipé de Mobile Partner sans
    //    terminal n'aurait rien à mettre en service.
    if (k === 'mobile-partner') {
      out.push(f.mobilePartnerOn && osPick.length === 0 && 'Choisissez au moins un terminal Mobile Partner, ou répondez « Non ».');
      out.push(f.mobilePartnerOn && osPick.length > 0 && (regimeProblem(osRegime, osDates, 'Mobile Partner') ?? false));
    }
    if (k === 'caisse' && f.stKind !== 'depot' && caissePick) {
      out.push(regimeProblem(caisseRegime, caisseDates, 'première caisse') ?? false);
      // ⚠️ La règle de la fiche du magasin, montrée sous les champs : la mise en service ne part pas avec un code que la
      //    route refuserait après coup.
      out.push(...neptingEntryProblems(caisseNepting.code, caisseNepting.url).map((p) => `Paiement par carte : ${p}.`));
    }
    return out.filter((x): x is string => !!x);
  };
  const missing = (): string | null => problemsOf(cur ?? '')[0] ?? null;
  // Les étapes qui ont encore un manque — ce que le récapitulatif liste, et ce qui verrouille le lancement.
  const gaps = stepList.map((st, i) => ({ st, i, problems: problemsOf(st.k) })).filter((g) => g.st.k !== 'recap' && g.problems.length > 0);
  const launchLocked = cur === 'recap' && gaps.length > 0;
  const next = () => {
    if (launchLocked) return;
    const m = missing(); if (m) { setErr(m); return; }
    setErr(null); if (step < stepList.length - 1) setStep(step + 1); else void execute();
  };
  const goStep = (i: number) => { setErr(null); setStep(i); };
  const back = () => { setErr(null); if (step > 0) setStep(step - 1); else if (!preset) { if (mode?.startsWith('client')) { setPhase('type'); setMode(null); } else { setPhase('entry'); setMode(null); } } };

  const pickCompany = (r: SireneResult) => set({ name: r.name || f.name, siren: r.siren ?? f.siren, siretHq: r.siret ?? f.siretHq, naf: r.naf ? normalizeNaf(r.naf) ?? r.naf : f.naf, legalForm: r.legalForm ?? f.legalForm, street: r.street ?? f.street, cp: r.cp ?? f.cp, city: r.city ?? f.city });
  const pickEstablishment = (r: SireneResult) => set({ stName: r.name || f.stName, stSiret: r.siret ?? f.stSiret, stStreet: r.street ?? f.stStreet, stCp: r.cp ?? f.stCp, stCity: r.city ?? f.stCity });

  /**
   * Pose le code Nepting saisi sur la caisse que l'affectation vient de numéroter, par la route de la fiche du magasin.
   * ⚠️ NON BLOQUANT, MAIS DIT : la caisse est affectée et son QR doit sortir ; un code non posé se pose ensuite depuis la
   *    fiche du magasin, et le rapport le nomme.
   * ⚠️ Le n° est celui que le serveur vient d'ATTRIBUER, jamais un « 01 » supposé.
   */
  const poseCaisseNepting = async (storeId: string, registerNumber: number | null): Promise<{ lines: string[]; warn: string[] }> => {
    const merchantCode = normalizeMerchantCode(caisseNepting.code);
    const later = 'Posez-le depuis la fiche du magasin, carte « Paiement par carte (Nepting) ».';
    if (registerNumber == null) return { lines: [], warn: [`Code commerçant NON enregistré — l’affectation n’a pas rendu de n° de caisse. ${later}`] };
    const registerId = registerIdOf(registerNumber);
    try {
      const r = await api.setStoreNeptingTerminal(storeId, registerId, { merchantCode, webServiceUrl: caisseNepting.url.trim() || null });
      const line = `caisse ${codeCaisse(registerId)} : code ${merchantCode}`;
      return r.descend.ok
        ? { lines: [line, r.descend.note], warn: [] }
        : { lines: [line], warn: [`Écrit dans la base du magasin, pas encore transmis aux caisses : ${r.descend.note}. Re-déclarez le magasin au back-office (section Données) pour le transmettre.`] };
    } catch (e) {
      return { lines: [], warn: [`Code commerçant NON enregistré — ${(e as Error).message}. ${later}`] };
    }
  };

  const execute = async () => {
    const isClient = mode !== 'store';
    const makesStore = mode === 'client-mono' || mode === 'store';
    const assignsMobilePartner = makesStore && f.mobilePartnerOn && osPick.length > 0;
    // ⚠️ Une caisse ne s'affecte qu'à un POINT DE VENTE : un dépôt n'encaisse pas. La garde est ici et
    // pas seulement à l'écran — changer de type après avoir choisi une caisse ne doit pas la poser quand même.
    const assignsCaisse = makesStore && f.stKind !== 'depot' && !!caissePick;
    const posesNepting = assignsCaisse && !!caisseNepting.code.trim();
    // Les groupes à retenir — seulement pour une application que ce magasin aura, par la NATURE de l'appareil qui
    // l'installe : un dépôt n'a pas de caisse, et un magasin sans Mobile Partner n'a pas de terminal à faire suivre.
    const groupsToSet = installableApplications().flatMap((a) => {
      const g = f.updateGroups[a.slug];
      const applies = a.nature === 'caisse' ? f.stKind !== 'depot' : a.nature === 'mobile-partner' ? f.mobilePartnerOn : false;
      return makesStore && g && applies ? [{ slug: a.slug, label: a.name, group: g }] : [];
    });
    const defs = [
      ...(isClient ? [{ label: 'Création du registre client' }] : []),
      ...(makesStore ? [
        { label: 'Création du magasin (numérotation)' },
        // ⚠️ AVANT la base : la mise en service fait descendre le groupe des caisses qu'elle trouve enregistré.
        ...(groupsToSet.length ? [{ label: 'Groupes de mise à jour' }] : []),
        { label: 'Mise en service de la base (schéma + gardes NF525)' },
      ] : []),
      ...(assignsCaisse ? [
        { label: 'Affectation de la première caisse' },
        ...(posesNepting ? [{ label: 'Code commerçant Nepting de la caisse' }] : []),
        { label: "Génération du jeton d'appairage" },
      ] : []),
      ...(assignsMobilePartner ? [{ label: osPick.length > 1 ? `Affectation des ${osPick.length} Mobile Partner (parc)` : "Affectation de l'Mobile Partner (parc)" }] : []),
    ];
    setPhase('run'); setRunError(null); setDone(false); setCaisseJeton(null); setRunSteps(defs.map((d) => ({ label: d.label, status: 'pending' })));
    const upd = (i: number, patch: Partial<{ status: 'pending' | 'run' | 'ok' | 'error'; detail: string; lines: string[]; warn: string[] }>) => setRunSteps((prev) => prev.map((s, j) => (j === i ? { ...s, ...patch } : s)));
    let i = 0;
    try {
      let tid = clientId;
      let openedName = clientName;
      if (isClient) {
        upd(i, { status: 'run' });
        const r = await api.createTenant({ name: f.name.trim(), siren: f.siren || undefined, businessKind: f.businessKind || undefined, topology: mode === 'client-multi' ? 'multi' : 'mono', commercialName: f.commercialName || undefined, siretHq: f.siretHq || undefined, naf: f.naf || undefined, legalForm: f.legalForm || undefined, address: { voie: f.street, cp: f.cp, ville: f.city }, contact: { email: f.contactEmail }, vatFranchise: f.vatFranchise, vatNumberAssigned: f.vatFranchise && f.vatNumberAssigned });
        tid = r.tenant.id; setCreatedClientId(tid); upd(i, { status: 'ok', detail: `« ${r.tenant.name} »` }); i++;
        openedName = r.tenant.name;
      }
      if (makesStore && tid) {
        upd(i, { status: 'run' });
        // ⚠️ syncMode DOIT partir : l'étape « Synchronisation » le collecte et le récap l'affiche — le jeter
        // silencieusement ici serait une saisie mensongère (§23.0, bug relevé en passe de contrôle).
        const rs = await api.createStore({ tenantId: tid, name: f.stName.trim(), gln: f.stGln, kind: f.stKind, businessKind: f.businessKind || undefined, siret: f.stSiret || undefined, syncMode: f.syncMode, currency: f.currency, address: { voie: f.stStreet, cp: f.stCp, ville: f.stCity } });
        // ⚠️ La prévision affichée à l'étape « magasin » est confrontée au numéro RÉELLEMENT attribué.
        // Un écart n'est pas une erreur — c'est qu'un autre magasin a été créé entre-temps — mais il
        // doit se VOIR : l'opérateur a lu un numéro, il repartirait avec le mauvais en tête.
        if (prevu && (prevu.sCode !== rs.store.sCode || prevu.radicalEan !== rs.store.radicalEan)) {
          const bouge = [
            prevu.sCode !== rs.store.sCode ? `n° magasin S${prevu.sCode} → S${rs.store.sCode}` : null,
            prevu.radicalEan !== rs.store.radicalEan ? `radical EAN ${fullEanRadical(prevu.radicalEan)} → ${fullEanRadical(rs.store.radicalEan)}` : null,
          ].filter(Boolean).join(' · ');
          setEcartCompteur(`Les compteurs ont avancé pendant la saisie — une autre création est passée avant celle-ci. ${bouge}. Ce sont les valeurs de droite qui font foi.`);
        }
        upd(i, { status: 'ok', detail: `N° ${rs.store.sCode} · radical EAN ${fullEanRadical(rs.store.radicalEan)}` }); i++;
        setOpenedStore({ tenantId: tid, tenantName: openedName, gln: f.stGln });
        if (groupsToSet.length) {
          upd(i, { status: 'run' });
          // ⚠️ NON BLOQUANT, MAIS DIT : un groupe non retenu laisse les appareils sur le flux public — le magasin
          //    fonctionne, et le groupe se choisit ensuite depuis sa fiche.
          const lines: string[] = [];
          const warn: string[] = [];
          for (const g of groupsToSet) {
            try {
              await api.setStoreUpdateGroup(rs.store.id, g.slug, g.group.token);
              lines.push(`${g.label} : ${g.group.name}`);
            } catch (e) {
              warn.push(`${g.label} : groupe « ${g.group.name} » NON retenu — ${(e as Error).message}. Les appareils suivront le flux public ; choisissez le groupe depuis la fiche du magasin.`);
            }
          }
          upd(i, { status: 'ok', lines, warn }); i++;
        }
        upd(i, { status: 'run' });
        const settings: Record<string, string> = {
          // ⚠️ CLES ET FORMAT DE LA TABLETTE (aligne 2026-08-13). Le wizard ecrivait `ticket_header`,
          // `voucher_validity_months`… que Sliderz Retail ne lit pas : l'en-tete de ticket et les validites
          // saisis ici n'atteignaient jamais la caisse. Les validites sont un BLOB JSON {val, unit, text} —
          // valeur et unite separees, jamais converties en jours.
          'ticket.header': f.ticketHeader, 'ticket.footer': f.ticketFooter,
          // ⚠️ LE CALENDRIER D’EXPLOITATION — mêmes clés que celles que le back-office LIT déjà
          //    (`store-calendar.ts`). Rien à migrer, rien à ajouter au catalogue : elles existent, et
          //    il s’agit uniquement de les SAISIR à la création plutôt que de laisser le repli faire
          //    illusion sur un magasin ultramarin ou fermé le lundi.
          'store.timezone': f.timezone,
          'store.open_days': f.openDays.join(','),
          'validity.credit_note': JSON.stringify(f.voucher), 'validity.gift': JSON.stringify(f.gift),
          // ⚠️ L'EXPÉDITEUR EST SEMÉ VIDE, ET C'EST VOULU : absent, la caisse retomberait sur l'adresse de l'ÉDITEUR
          //    (`MAIL_DEFAULTS.from`). L'assistant ne demande plus de compte d'envoi (aucune clé,
          //    aucun compte ici) — le compte SMS et l'expéditeur se règlent depuis la fiche du magasin.
          'mail.from': '',
          // Programme de fidelite COMPLET (etape dediee) — memes cles et memes formats que la tablette :
          // booleens en '1'/'0', paliers en blob JSON. Ce qui n'est pas seme ici garderait les defauts de
          // la caisse, donc une valeur que personne n'a choisie.
          'fid.enabled': f.fid.enabled ? '1' : '0',
          ...(f.fid.enabled ? {
            'fid.mode': f.fid.mode,
            'fid.use_from': f.fid.useFrom,
            'fid.conversion': f.fid.conversion,
            'fid.welcome_bonus': f.fid.welcomeBonus,
            'fid.tiers_on': f.fid.tiersOn ? '1' : '0',
            'fid.window_days': f.fid.windowDays,
            'fid.tiers': JSON.stringify(f.fid.tiers),
            'fid.keep_tier': f.fid.keepTier ? '1' : '0',
            'fid.protect_days': f.fid.protectDays,
            // ⚠️ En cashback la tablette FORCE ces deux-la : on seme la valeur EFFECTIVE, pas celle du
            // formulaire, sinon la base garderait un reglage que l'ecran affichait comme impose.
            'fid.allow_cumul': f.fid.mode === 'cashback' || f.fid.allowCumul ? '1' : '0',
            'fid.print_voucher': f.fid.mode === 'cashback' ? '0' : f.fid.printVoucher ? '1' : '0',
            'fid.forbid_immediate': f.fid.forbidImmediate ? '1' : '0',
          } : {}),
          mobile_partner_enabled: f.mobilePartnerOn ? '1' : '0',
          // Règle « synchro périodique » (§13.1quater) — semée dans les settings du magasin, la caisse applique. Les délais
          // ont été jugés à l'étape « Synchronisation » (`offlineRuleError`) : ils ne peuvent pas arriver vides ici.
          'api.offline_rule': f.offlineRule ? '1' : '0',
          'api.offline_warn_days': f.offlineRule ? f.offlineWarnDays : '0',
          'api.offline_lock_days': f.offlineRule ? f.offlineLockDays : '0',
        };
        const rp = await api.provisionStore(rs.store.id, settings);
        // Le rapport DIT ce qui a réellement été écrit, fait par fait — y compris quand le métier n'a
        // pas été résolu et que seul le socle a été injecté (§23.0 : jamais un « en service » qui
        // masque un manque). Une seule ligne de détail ne pouvait en porter qu'un seul.
        upd(i, {
          status: 'ok',
          lines: [
            `base ${rp.dbName}`,
            rp.schemaVersion != null ? `schéma v${rp.schemaVersion} · gardes NF525 posées` : 'schéma appliqué',
            rp.refsNote ?? (rp.seededRefs !== undefined ? `${rp.seededRefs} lignes de référentiels injectées` : 'référentiels non rapportés'),
            `réglages du magasin écrits (${Object.keys(settings).length} clés)`,
            rp.boDeclared ? 'déclaré au back-office' : null,
            rp.boDeclared && !rp.identityError && !rp.identityMissing?.length ? 'identité légale descendue' : null,
            rp.adminAccountPushed ? "compte d'administration descendu" : null,
            rp.vocabularyPushed !== undefined ? `${rp.vocabularyPushed} mots de vocabulaire descendus` : null,
            rp.ediDeclared ? "déclaré à l'EDI (identité de site)" : null,
          ].filter((x): x is string => !!x),
          // ⚠️ Ces deux échecs n'ANNULENT PAS la création (best-effort côté serveur) mais ils changent
          // ce qu'il reste à faire : sans déclaration au BO, aucune caisse ne pourra synchroniser.
          // Ils étaient renvoyés et jetés — un magasin à moitié annoncé paraissait donc prêt.
          warn: [
            !rp.boDeclared ? `Back-office NON déclaré — les caisses ne pourront pas synchroniser. ${rp.boError ?? ''}`.trim() : null,
            // ⚠️ Ces quatre étapes n'ont lieu qu'une fois le registre déclaré : sans lui, c'est lui qu'on nomme,
            //    et « Déclarer au back-office » rattrape le tout.
            rp.boDeclared && rp.identityError ? `Identité légale NON descendue — les tickets seraient scellés sans raison sociale ni SIRET. ${rp.identityError}` : null,
            rp.boDeclared && !rp.identityError && rp.identityMissing?.length ? `Identité légale incomplète — manquent : ${rp.identityMissing.join(', ')}. Complétez le dossier, puis « Déclarer au back-office ».` : null,
            rp.boDeclared && !rp.adminAccountPushed ? `Compte d'administration NON descendu — la caisse ne pourra pas être paramétrée. ${rp.adminAccountError ?? ''}`.trim() : null,
            rp.boDeclared && rp.seedError ? `Amorçage du journal en échec — les caisses ne recevront pas tout. ${rp.seedError}` : null,
            rp.boDeclared && rp.vocabularyError ? `Vocabulaire du métier NON descendu. ${rp.vocabularyError}` : null,
            rp.boDeclared && rp.updateGroupError ? `Groupe de mise à jour des caisses NON descendu — elles suivent le flux public. ${rp.updateGroupError}` : null,
            !rp.ediDeclared ? `EDI non déclaré — aucun flux fournisseur pour ce site. ${rp.ediError ?? ''}`.trim() : null,
          ].filter((x): x is string => !!x),
        }); i++;
        // PREMIÈRE CAISSE — affectation (le serveur attribue le n° R de façon atomique), puis jeton.
        // ⚠️ DEUX étapes distinctes à l'écran parce que ce sont deux écritures : si la seconde échoue,
        // la caisse est bien affectée et c'est seulement le QR qui manque — le dire évite de croire
        // qu'il faut tout recommencer.
        if (assignsCaisse && caissePick) {
          upd(i, { status: 'run' });
          const dev = caissePool.find((d) => d.id === caissePick);
          const ra = await api.deviceAction(caissePick, { action: 'assign', storeId: rs.store.id, rentalType: caisseRegime, ...(caisseRegime === 'longue' ? { rentalStart: caisseDates.from, rentalEnd: caisseDates.to } : {}) });
          const numero = (ra as { registerNumber?: number | null }).registerNumber ?? null;
          upd(i, { status: 'ok', detail: `${dev?.model ?? 'caisse'}${numero != null ? ` · n° ${numero}` : ''}` }); i++;
          if (posesNepting) {
            upd(i, { status: 'run' });
            upd(i, { status: 'ok', ...(await poseCaisseNepting(rs.store.id, numero)) }); i++;
          }
          upd(i, { status: 'run' });
          const jeton = await api.createPairing(caissePick);
          setCaisseJeton({ ...jeton, registerNumber: numero });
          upd(i, { status: 'ok', detail: 'usage unique · 24 h' }); i++;
        }
        // Affectation RÉELLE des Mobile Partner choisis au parc → liés au magasin créé, au régime choisi.
        // ⚠️ SÉQUENTIEL et non en parallèle : chaque affectation est une écriture au parc, et si la
        // troisième échoue il faut savoir que les deux premières sont passées. Le message d'erreur
        // nomme donc l'appareil fautif — sans quoi l'opérateur ignorerait où s'est arrêtée la série.
        if (assignsMobilePartner) {
          upd(i, { status: 'run' });
          const faits: string[] = [];
          for (const id of osPick) {
            const dev = osPool.find((d) => d.id === id);
            const nom = dev?.serial ?? dev?.model ?? id.slice(0, 8);
            try {
              await api.deviceAction(id, { action: 'assign', storeId: rs.store.id, rentalType: osRegime, ...(osRegime === 'longue' ? { rentalStart: osDates.from, rentalEnd: osDates.to } : {}) });
            } catch (e) {
              throw new Error(`${nom} non affecté : ${(e as Error).message}${faits.length ? ` (déjà affectés : ${faits.join(', ')})` : ''}`, { cause: e });
            }
            faits.push(nom);
          }
          upd(i, { status: 'ok', detail: faits.join(' · ') }); i++;
        }
      }
      setDone(true);
    } catch (e) {
      setRunError((e as Error).message);
      setRunSteps((prev) => { const idx = prev.findIndex((s) => s.status === 'run'); return prev.map((s, j) => (j === idx ? { ...s, status: 'error' } : s)); });
    }
  };

  const headerIcon = phase === 'run' ? IC.refresh : mode === 'store' ? IC.building : mode ? IC.users : IC.plus;
  // ⚠️ « Que créez-vous ? » et non « Nouveau » (maquette) : à l'entrée, l'écran POSE une question —
  // c'est ce qui fait comprendre que les trois tuiles sont des réponses, pas des raccourcis.
  const title = phase === 'entry' ? 'Que créez-vous ?' : phase === 'type' ? 'Type de base' : mode === 'store' ? 'Nouveau magasin' : mode === 'client-multi' ? 'Client multi-magasin' : 'Client mono-magasin';
  const crumb = `Créer${mode ? ` › ${mode === 'store' ? 'Nouveau magasin' : mode === 'client-multi' ? 'Client multi-magasin' : 'Client mono-magasin'}` : ''}${phase === 'run' ? ' › Exécution' : ''}`;
  const barW = phase === 'entry' ? 6 : phase === 'type' ? 12 : phase === 'run' ? 100 : Math.round(((step + 1) / stepList.length) * 100);
  const tile = (on = false): CSSProperties => ({ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', textAlign: 'left', padding: 18, borderRadius: 'var(--r-md)', cursor: 'pointer', border: `1px solid ${on ? 'var(--teal)' : 'var(--ink200)'}`, background: on ? 'var(--ok-bg)' : 'var(--card)', color: 'var(--ink900)' });
  const iconBox: CSSProperties = { width: 40, height: 40, borderRadius: 'var(--r-sm)', background: 'var(--ink100)', display: 'grid', placeItems: 'center', color: 'var(--teal)' };
  const seg = (on: boolean): CSSProperties => ({ flex: 1, height: 40, borderRadius: 'var(--r-sm)', cursor: 'pointer', border: `1px solid ${on ? 'var(--teal)' : 'var(--ink200)'}`, background: on ? 'var(--ok-bg)' : 'transparent', color: 'var(--ink900)', fontSize: 'var(--t-body)', fontWeight: 600 });

  return (
    <div className="modalwrap" onClick={onClose} style={{ padding: '28px 22px' }}>
      <div ref={panelRef} role="dialog" aria-modal="true" aria-labelledby={`${uid}-title`}
        onClick={(e) => e.stopPropagation()} style={{ width: 'min(97vw, 1000px)', maxHeight: 'calc(100vh - 56px)', display: 'flex', flexDirection: 'column', ...card, borderRadius: 'var(--r-lg)', boxShadow: '0 48px 110px -34px rgba(0,0,0,.8)', overflow: 'hidden' }}>
        {/* HEADER */}
        <div style={{ padding: '18px 24px 0', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
            <div style={{ width: 44, height: 44, borderRadius: 'var(--r-sm)', background: 'var(--ok-bg)', border: '1px solid rgba(45,212,191,.28)', display: 'grid', placeItems: 'center', color: 'var(--ok-fg)', flexShrink: 0, marginTop: 2 }}>{headerIcon}</div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={eyebrow}>{crumb}</div>
              <div id={`${uid}-title`} style={{ fontSize: 18, fontWeight: 700, letterSpacing: '-.02em', marginTop: 6 }}>{title}</div>
              <div style={{ fontSize: 'var(--t-dense)', color: 'var(--ink500)', marginTop: 4 }}>{phase === 'entry' ? 'Un client est un univers : il possède ses magasins, ses licences et ses utilisateurs. Un magasin n’existe jamais seul.' : phase === 'type' ? 'Le socle métier de la base. Seul Retail est disponible pour l\'instant.' : phase === 'run' ? 'Chaque étape est réelle et vérifiée.' : mode === 'store' ? 'Ajouter un magasin et mettre sa base en service.' : 'Créer le client et son 1ᵉʳ magasin d\'un coup.'}</div>
            </div>
            {phase === 'steps' && <div style={{ textAlign: 'right', flexShrink: 0 }}><div style={{ ...eyebrow, fontSize: 'var(--t-eyebrow)' }}>étape</div><div style={{ fontSize: 'var(--t-feature)', fontWeight: 800, letterSpacing: '-.03em' }}>{step + 1}<span style={{ fontSize: 'var(--t-lead)', color: 'var(--ink400)' }}> / {stepList.length}</span></div></div>}
            <button onClick={onClose} title="Fermer" className="btn-icon">✕</button>
          </div>
          <div style={{ height: 3, borderRadius: 'var(--r-r3)', background: 'var(--ink200)', margin: '16px 0 0', overflow: 'hidden' }}><div style={{ height: '100%', width: `${barW}%`, background: 'linear-gradient(90deg,var(--teal),#5ddfc4)', transition: 'width .2s' }} /></div>
        </div>

        {/* BODY */}
        <div style={{ padding: '20px 24px 24px', overflowY: 'auto', flex: 1, minHeight: 0 }}>
          {phase === 'entry' && (
            <>
              <div style={{ ...eyebrow, marginBottom: 10 }}>deux univers, deux parcours</div>
              {/* Libellés et descriptions REPRIS DE LA MAQUETTE (2026-08-12). Les nôtres résumaient le
                  geste (« créés d'un coup ») ; ceux-ci nomment ce qu'on obtient et dans quel ordre,
                  ce qui est la seule chose qui aide à choisir entre les trois. */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))', gap: 14, maxWidth: 1000 }}>
                {([
                  ['client-mono', IC.bag, 'Nouveau client · mono-magasin', 'Client + son unique magasin dans la même passe : identité, base, référentiels, numérotation, jeton d’appairage.'],
                  ['client-multi', IC.grid, 'Nouveau client · multi-magasin', 'Le client d’abord, sans magasin. On entre ensuite dans sa fiche pour créer les magasins un par un.'],
                  ['store', IC.building, 'Nouveau magasin', 'Pour un client déjà en service : le magasin hérite du métier, des référentiels et des licences.'],
                ] as const).map(([m, ic, name, desc]) => (
                  <button key={m} onClick={() => { setMode(m); setPhase(m === 'store' ? 'steps' : 'type'); setStep(0); }} style={tile()}>
                    <span style={iconBox}>{ic}</span>
                    <span style={{ fontSize: 'var(--t-strong)', fontWeight: 700, marginTop: 12 }}>{name}</span>
                    <span style={{ fontSize: 'var(--t-dense)', color: 'var(--ink500)', lineHeight: 1.55, marginTop: 8 }}>{desc}</span>
                    {/* Longueur du parcours, comptée sur la liste RÉELLE d'étapes (maquette) : on choisit
                        aussi en fonction du temps que ça prend. */}
                    <span className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink400)', marginTop: 12 }}>
                      {stepsFor(m, !!clientId).length} étapes
                    </span>
                  </button>
                ))}
              </div>
            </>
          )}

          {phase === 'type' && (
            <>
              <div style={{ ...eyebrow, marginBottom: 10 }}>type de base</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 12 }}>
                {([['retail', IC.tag, 'Retail', 'Prêt-à-porter, sport, optique…', true], ['chr', IC.building, 'CHR / Restauration', 'Bientôt', false], ['tel', IC.building, 'Téléphonie', 'Bientôt', false], ['bak', IC.building, 'Boulangerie', 'Bientôt', false]] as const).map(([k, ic, name, desc, on]) => (
                  <button key={k} disabled={!on} onClick={() => on && setPhase('steps')} style={{ ...tile(), cursor: on ? 'pointer' : 'not-allowed', opacity: on ? 1 : 0.5 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 9, width: '100%' }}><span style={iconBox}>{ic}</span><span style={{ fontSize: 'var(--t-control)', fontWeight: 700 }}>{name}</span>{!on && <><div style={{ flex: 1 }} /><span style={{ fontSize: 'var(--t-micro)', color: 'var(--ink500)' }}>bientôt</span></>}</div>
                    <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink500)', marginTop: 8 }}>{desc}</div>
                  </button>
                ))}
              </div>
            </>
          )}

          {phase === 'steps' && (
            <div style={{ display: 'grid', gridTemplateColumns: '230px minmax(0,1fr)', gap: 16, alignItems: 'start' }}>
              {/* Rail d'étapes */}
              <div style={{ ...card, padding: '12px 10px', position: 'sticky', top: 0 }}>
                <div style={{ ...eyebrow, fontSize: 'var(--t-eyebrow)', padding: '4px 10px 8px' }}>étapes</div>
                {stepList.map((s, i) => {
                  const cu = i === step;
                  // Une étape PASSÉE (ou toutes, au récapitulatif) avec un manque : « ! » en ambre, pas une coche.
                  const gap = !cu && (i < step || cur === 'recap') && gaps.some((g) => g.i === i);
                  const dn = i < step && !gap;
                  return (
                    <button key={s.k} onClick={() => goStep(i)} aria-current={cu ? 'step' : undefined} style={{ display: 'flex', alignItems: 'center', gap: 9, width: '100%', padding: '8px 10px', borderRadius: 'var(--r-sm)', border: 0, background: cu ? 'var(--ink100)' : 'transparent', cursor: 'pointer', textAlign: 'left' }}>
                      <span className="mono" data-step-mark={gap ? 'gap' : dn ? 'done' : undefined} style={{ width: 20, height: 20, borderRadius: 'var(--r-xs)', display: 'grid', placeItems: 'center', fontSize: 'var(--t-meta)', fontWeight: 700, flexShrink: 0, background: gap ? 'var(--wa-bg)' : dn ? 'var(--ok-bg)' : cu ? 'var(--teal)' : 'var(--ink100)', color: gap ? 'var(--wa-fg)' : dn ? 'var(--ok-fg)' : cu ? '#08130f' : 'var(--ink500)' }}>{gap ? '!' : dn ? '✓' : i + 1}</span>
                      <span style={{ flex: 1, fontSize: 'var(--t-body)', fontWeight: cu ? 600 : 500, color: cu ? 'var(--ink900)' : 'var(--ink600)' }}>{s.t}</span>
                    </button>
                  );
                })}
              </div>

              {/* Carte de contenu */}
              <div style={{ ...card, padding: 20 }}>
                {cur === 'ident' && (
                  <>
                    <SireneBox title="Rechercher la société" sub="annuaire des entreprises · Sirene" placeholder="Nom, enseigne, SIREN ou SIRET — 3 caractères min." kind="societe" onPick={pickCompany} />
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 16px' }}>
                      <Field label="Raison sociale"><input className="field-input" value={f.name} onChange={(e) => set({ name: e.target.value })} placeholder="Maison Vireau SAS" /></Field>
                      <Field label="Nom commercial"><input className="field-input" value={f.commercialName} onChange={(e) => set({ commercialName: e.target.value })} /></Field>
                      <Field label="SIREN"><input className="mono field-input" value={f.siren} onChange={(e) => set({ siren: e.target.value.replace(/\D/g, '').slice(0, 9) })} placeholder="9 chiffres" /></Field>
                      <Field label="SIRET du siège" hint="Requis avant la première vente (NF525)"><input className="mono field-input" value={f.siretHq} onChange={(e) => set({ siretHq: e.target.value })} placeholder="14 chiffres" /></Field>
                      <Field label="Code NAF / APE"><input className="mono field-input" value={f.naf} onChange={(e) => set({ naf: e.target.value })} placeholder="47.71Z" /></Field>
                      <Field label="TVA intracom. (calculée)"><input className="mono" style={{ ...fieldSt, color: 'var(--ink500)' }} value={companyVatNumber(f.siren, { franchise: f.vatFranchise, numberAssigned: f.vatNumberAssigned }) ?? (f.vatFranchise && !f.vatNumberAssigned ? 'aucun — franchise en base' : '')} readOnly placeholder="— depuis le SIREN" /></Field>
                      {/* Le RÉGIME DE TVA se décide à la création (décision du 28/10) : il descend à chaque magasin, et les caisses
                          facturent sans TVA en franchise, mention de l'art. 293 B comprise. */}
                      <div style={{ gridColumn: '1 / -1' }}>
                        <span id={`${uid}-vat`} className="field-title" style={lbl}>Régime de TVA</span>
                        <div role="radiogroup" aria-labelledby={`${uid}-vat`} style={{ display: 'flex', gap: 6, flexWrap: 'wrap', maxWidth: 520 }}>
                          {([[false, 'Assujetti à la TVA'], [true, 'Franchise en base (art. 293 B du CGI)']] as const).map(([k, l], i) => (
                            <button key={l} type="button" role="radio" aria-checked={f.vatFranchise === k} onClick={() => set({ vatFranchise: k })} style={seg(f.vatFranchise === k)}
                              {...rovingRadio({ index: i, count: 2, checked: f.vatFranchise === k, anyChecked: true, onPick: (n) => set({ vatFranchise: n === 1 }) })}>
                              {l}
                            </button>
                          ))}
                        </div>
                        {f.vatFranchise && (
                          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 10, fontSize: 'var(--t-note)', color: 'var(--ink700)', lineHeight: 1.45, cursor: 'pointer' }}>
                            <input type="checkbox" checked={f.vatNumberAssigned} onChange={(e) => set({ vatNumberAssigned: e.target.checked })} style={{ marginTop: 2 }} />
                            <span>Un numéro de TVA intracommunautaire lui a été attribué — achats ou ventes dans l’Union au-delà des seuils, ou option. Sinon, elle n’en a pas.</span>
                          </label>
                        )}
                      </div>
                      <Field label="Adresse du siège" wide><input className="field-input" value={f.street} onChange={(e) => set({ street: e.target.value })} /></Field>
                      <Field label="Code postal"><input className="mono field-input" value={f.cp} onChange={(e) => set({ cp: e.target.value })} /></Field>
                      <Field label="Ville"><input className="field-input" value={f.city} onChange={(e) => set({ city: e.target.value })} /></Field>
                      <Field label="Contact principal" wide><input className="mono field-input" value={f.contactEmail} onChange={(e) => set({ contactEmail: e.target.value })} placeholder="prenom.nom@enseigne.fr" /></Field>
                    </div>
                  </>
                )}

                {cur === 'metier' && (
                  <>
                    <div style={{ ...eyebrow, marginBottom: 10 }}>type de commerce — hérité par les magasins</div>
                    {tradesErr && <div className="err">Types de commerce non chargés : {tradesErr}</div>}
                    {!tradesErr && trades === null && <div className="muted">Chargement des types de commerce…</div>}
                    {trades?.length === 0 && (
                      <EmptyState compact icon="store" title="Aucun type de commerce" note="Créez-en un dans Référentiels › Types de commerce — c'est lui qui décide des listes injectées dans la base du magasin." />
                    )}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 10 }}>
                      {(trades ?? []).map((t) => (
                        <button key={t.id} onClick={() => set({ businessKind: t.label })} style={{ ...tile(f.businessKind === t.label), padding: '13px 14px' }}>
                          <span style={{ fontSize: 'var(--t-lead)', fontWeight: 700 }}>{t.label}</span>
                          {t.description && <span style={{ fontSize: 'var(--t-meta)', color: 'var(--ink500)', marginTop: 3, lineHeight: 1.4 }}>{t.description}</span>}
                          <span className="mono" style={{ fontSize: 'var(--t-micro)', color: 'var(--ink400)', marginTop: 5 }}>{t.lists.length} listes · {linesOfTrade(t)} lignes</span>
                        </button>
                      ))}
                    </div>
                    {/* Conséquence du choix, énoncée AVANT de valider : ce sont ces listes qui seront écrites
                        dans la base du magasin à sa mise en service (maquette : « listes injectées »). */}
                    {picked && (
                      <div style={{ marginTop: 14, padding: '11px 13px', borderRadius: 'var(--r-sm)', border: '1px solid var(--ink200)', background: 'var(--in-bg)' }}>
                        <div style={{ fontSize: 'var(--t-meta)', fontWeight: 700, color: 'var(--in-fg)', marginBottom: 7 }}>Listes injectées dans la base de ce magasin</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                          {picked.lists.map((k) => (
                            <span key={k} className="mono" style={{ fontSize: 'var(--t-micro)', padding: '2px 8px', borderRadius: 'var(--r-xs)', background: 'var(--canvas)', color: 'var(--ink700)', border: '1px solid var(--ink200)' }}>
                              {listLabel(k)} · {linesOfList(k)}
                            </span>
                          ))}
                        </div>
                        <div style={{ fontSize: 'var(--t-sub)', color: 'var(--in-fg)', marginTop: 8, lineHeight: 1.5 }}>
                          Assemblage défini dans <b>Référentiels › Types de commerce</b> — modifiable là-bas, jamais ici.
                        </div>
                      </div>
                    )}
                  </>
                )}

                {cur === 'pick' && (
                  <>
                    <label htmlFor={`${uid}-client`} style={lbl}>Client de rattachement</label>
                    <input id={`${uid}-client`} value={pcQ} onChange={(e) => setPcQ(e.target.value)} placeholder="Nom du client, métier…" style={{ ...fieldSt, marginBottom: 12 }} />
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 340, overflowY: 'auto' }}>
                      {(tenants ?? []).filter((t) => !pcQ.trim() || (t.name + ' ' + (t.businessKind ?? '')).toLowerCase().includes(pcQ.trim().toLowerCase())).map((t) => (
                        <button key={t.id} onClick={() => { setClientId(t.id); setClientName(t.name); }} style={{ ...tile(clientId === t.id), padding: '11px 14px' }}>
                          <span style={{ fontSize: 'var(--t-title)', fontWeight: 700 }}>{t.name}</span>
                          <span style={{ fontSize: 'var(--t-note)', color: 'var(--ink500)', marginTop: 2 }}>{t.businessKind ?? '—'} · {t.storeCount} magasin(s)</span>
                        </button>
                      ))}
                      {sourceErr && <div className="err">{sourceErr}</div>}
                      {!sourceErr && tenants === null && <div className="muted">Chargement des clients…</div>}
                      {!sourceErr && tenants && tenants.length === 0 && <EmptyState compact icon="users" title="Aucun client" note="Créez d’abord un client — ce parcours ajoute un magasin à un client existant." />}
                    </div>
                  </>
                )}

                {cur === 'store' && (
                  <>
                    {clientName && <div style={{ fontSize: 'var(--t-dense)', color: 'var(--ink500)', marginBottom: 12 }}>Rattaché à <b style={{ color: 'var(--ink700)' }}>{clientName}</b>.</div>}
                    <SireneBox title="Rechercher l'établissement" sub="un établissement = un point de vente" placeholder="Nom de l'enseigne, SIRET, ou nom + ville…" kind="etablissement" onPick={pickEstablishment} />
                    {hqAddr && (
                      <button type="button" onClick={useHq} className="btn-ghost" style={{ height: 32, padding: '0 13px', marginBottom: 14, borderRadius: 'var(--r-sm)', fontSize: 'var(--t-dense)', fontWeight: 600 }}
                        title="Le magasin est au siège ? Reprendre son adresse et son SIRET.">Reprendre le siège — {hqAddr}</button>
                    )}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 16px' }}>
                      <Field label="Nom du magasin"><input className="field-input" value={f.stName} onChange={(e) => set({ stName: e.target.value })} placeholder="Colmar Centre" /></Field>
                      <FieldGroup label="Type"><div style={{ display: 'flex', gap: 6 }}>{(['store', 'depot'] as const).map((k) => <button key={k} onClick={() => set({ stKind: k })} style={seg(f.stKind === k)}>{k === 'store' ? 'Point de vente' : 'Dépôt'}</button>)}</div></FieldGroup>
                      <Field label="GLN" hint="13 chiffres — attribué par GS1, jamais par Sirene"><input className="mono field-input" value={f.stGln} onChange={(e) => set({ stGln: e.target.value })} placeholder="3760000000000" /></Field>
                      <Field label="SIRET de l'établissement"><input className="mono field-input" value={f.stSiret} onChange={(e) => set({ stSiret: e.target.value })} placeholder="14 chiffres" /></Field>
                      <Field label="Adresse de l'établissement" wide><input className="field-input" value={f.stStreet} onChange={(e) => set({ stStreet: e.target.value })} placeholder="12 route de Rouffach" /></Field>
                      <Field label="Code postal"><input className="mono field-input" value={f.stCp} onChange={(e) => set({ stCp: e.target.value })} placeholder="68000" /></Field>
                      <Field label="Ville"><input className="field-input" value={f.stCity} onChange={(e) => set({ stCity: e.target.value })} placeholder="Colmar" /></Field>
                      {/* ⚠️ LA DEVISE SE CHOISIT ICI, ET UNE SEULE FOIS DANS LA VIE DU MAGASIN. Elle décide
                          de ce que VEULENT DIRE tous les entiers qui seront écrits dans sa base : la changer
                          ensuite les réinterpréterait tous, en silence. Une garde en base le refuse dès que
                          le magasin a une base — d'où l'avertissement, qui dit la contrainte AVANT le clic
                          plutôt que de la faire découvrir par un refus (§23.0).
                          ⚠️ La liste vient du RÉFÉRENTIEL (`devises`, écran SA-10), jamais d'une constante :
                          ajouter une monnaie est une écriture, pas une livraison. */}
                      <Field label="Devise" wide hint="Choisie une fois pour toutes : elle ne pourra plus être modifiée après la mise en service.">
                        {devises === null
                          ? <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink500)' }}>Lecture des devises…</div>
                          : devises.length === 0
                            ? <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink500)' }}>Référentiel « devises » vide — le magasin sera créé en euros.</div>
                            : (
                              <select className="field-input" value={f.currency} onChange={(e) => set({ currency: e.target.value })}>
                                {devises.map((d) => (
                                  <option key={d.code} value={d.code}>
                                    {d.label} ({d.code}){d.fraction === 2 ? '' : ` — ${d.fraction} décimale${d.fraction > 1 ? 's' : ''}`}
                                  </option>
                                ))}
                              </select>
                            )}
                      </Field>
                    </div>
                    {/* Bloc maquette « Attribué automatiquement » : on PRÉVISUALISE ce que la mise en service
                        va figer, au lieu de le résumer en prose.
                        ⚠️ Les numéros affichés sont une PRÉVISION calculée sur l'annuaire réel, jamais une
                        réservation : c'est le serveur qui numérote à l'insertion. Ils portent donc la mention
                        « prévu », et l'écart éventuel est SIGNALÉ au retour de la création (§23.0 : une
                        prévision annoncée comme telle est honnête, une prévision présentée comme un fait ne
                        l'est pas). Le n° de caisse, lui, reste inconnaissable ici — il naît à l'appairage. */}
                    <div style={{ marginTop: 16, borderRadius: 'var(--r-sm)', border: '1px solid var(--ink200)', overflow: 'hidden' }}>
                      <div style={{ padding: '9px 13px', background: 'var(--ink100)', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ ...eyebrow }}>attribué automatiquement</span>
                        <span style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)' }}>· rien à saisir · figé dès la première vente</span>
                        {/* La mention vit DANS l'en-tête du bloc, pas en note de bas : elle qualifie les
                            quatre chiffres, et une réserve qu'il faut chercher n'est pas une réserve. */}
                        {prevu && <span className="mono" style={{ fontSize: 'var(--t-micro)', color: 'var(--wa-fg)', background: 'var(--wa-bg)', padding: '1px 7px', borderRadius: 'var(--r-pill)' }}>prévu</span>}
                        {prevuErr && <span style={{ fontSize: 'var(--t-micro)', color: 'var(--da-fg)' }}>numéros non prévisibles : {prevuErr}</span>}
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 1, background: 'var(--ink200)' }}>
                        {[
                          ['N° magasin', prevu ? `S${prevu.sCode}` : 'S<n>', 'unique par client — racine des compteurs'],
                          ['1ʳᵉ caisse', 'R1', 'numérotée à l’appairage'],
                          // La racine des pièces se COMPOSE des deux : le n° magasin prévu, et R1 puisque
                          // c'est la première caisse que ce magasin appairera.
                          ['Racine des pièces', prevu ? `S${prevu.sCode}R1` : 'S<n>R<caisse>', 'entre dans chaque numéro fiscal'],
                          ['Radical EAN', prevu ? fullEanRadical(prevu.radicalEan)! : '2 + 5 chiffres', 'ouvre chaque code-barres interne'],
                          // ⚠️ LE N° CLIENT EST UNE SÉQUENCE MÉTIER, PAS FISCALE : un trou y est toléré
                          //    (un client n'est pas une pièce justificative). Il est montré ici pour le
                          //    FORMAT, pas comme une garantie de continuité — d'où la note.
                          ['N° client', prevu ? `CLI-S${prevu.sCode}R1-000001` : 'CLI-S<n>R<caisse>-000000', 'attribué par la caisse · R0 réservé au serveur'],
                        ].map(([k, v, note]) => (
                          <div key={k} style={{ background: 'var(--card)', padding: '10px 13px' }}>
                            <div className="mono" style={{ fontSize: 'var(--t-eyebrow)', color: 'var(--ink500)' }}>{k}</div>
                            <div className="mono" style={{ fontSize: 'var(--t-lead)', fontWeight: 700, marginTop: 3 }}>{v}</div>
                            <div style={{ fontSize: 'var(--t-micro)', color: 'var(--ink500)', marginTop: 3, lineHeight: 1.4 }}>{note}</div>
                          </div>
                        ))}
                      </div>
                      <div style={{ padding: '10px 13px', borderTop: '1px solid var(--ink200)' }}>
                        <div className="mono" style={{ fontSize: 'var(--t-eyebrow)', letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--ink500)', marginBottom: 6 }}>compteurs de départ</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                          {['Ticket', 'Facture', 'Clôture Z', 'Avoir', 'Chèque cadeau', 'Acompte', 'Service'].map((c) => (
                            <span key={c} className="mono" style={{ fontSize: 'var(--t-micro)', padding: '2px 8px', borderRadius: 'var(--r-xs)', background: 'var(--ink100)', color: 'var(--ink700)' }}>{c} · 000001</span>
                          ))}
                        </div>
                        <div style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)', marginTop: 7, lineHeight: 1.5 }}>
                          Chaque série <b>fiscale</b> démarre à 1 et avance <b>sans trou</b> : la continuité est vérifiable par l'administration, une remise à zéro est impossible.
                          {' '}Le <b>n° client</b> suit le même format par confort de lecture, mais relève d'une séquence <b>métier</b> : un trou y est sans conséquence.
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {cur === 'sync' && (
                  <>
                    <div style={{ ...eyebrow, marginBottom: 10 }}>synchronisation BO ↔ caisse</div>
                    <span id={`${uid}-sync`} className="field-title" style={lbl}>Mode de synchronisation</span>
                    <div role="group" aria-labelledby={`${uid}-sync`} style={{ display: 'flex', gap: 6, maxWidth: 380 }}>{SYNC_MODES.map((m) => <button key={m.k} onClick={() => set({ syncMode: m.k })} style={seg(f.syncMode === m.k)}>{m.label}</button>)}</div>
                    <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink500)', marginTop: 14, lineHeight: 1.5 }}>{SYNC_MODES.find((m) => m.k === f.syncMode)?.desc}</div>
                    {/* ⚠️ LE SENS DES VENTES NE SE RÈGLE PAS : l'ancienne phrase disait « bidirectionnel remonte aussi les ventes »,
                        or une vente remonte toujours, quel que soit le mode. */}
                    <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink500)', marginTop: 6, lineHeight: 1.5 }}>Une vente naît en caisse et remonte toujours, quel que soit le mode ; hors ligne, la caisse continue d’encaisser et rejoue sa file à la reconnexion.</div>

                  </>
                )}

                {cur === 'periodic' && (
                  <>
                    <div style={{ ...eyebrow, marginBottom: 10 }}>synchro périodique obligatoire</div>
                    {/* La règle (§13.1quater) — PAR COMMERCE, jamais codée en dur dans la tablette : semée dans les
                        settings du magasin (api.offline_*), la caisse applique. */}
                    <span id={`${uid}-offline`} className="field-title" style={lbl}>Synchronisation périodique obligatoire</span>
                    <div role="group" aria-labelledby={`${uid}-offline`} style={{ display: 'flex', gap: 6, maxWidth: 380 }}>
                      {([[true, 'Activée'], [false, 'Désactivée (profil itinérant)']] as const).map(([k, l]) => (
                        <button key={String(k)} onClick={() => set({ offlineRule: k })} style={seg(f.offlineRule === k)}>{l}</button>
                      ))}
                    </div>
                    {/* Coupée, la règle ne demande rien : les délais se GRISENT (maquette), ils ne disparaissent pas. */}
                    <div data-offline-delays={f.offlineRule ? 'on' : 'off'} style={{ display: 'flex', gap: 12, marginTop: 12, maxWidth: 380, ...(f.offlineRule ? null : { opacity: 0.45 }) }}>
                      <div style={{ flex: 1 }}>
                        <label htmlFor={`${uid}-warn`} style={lbl}>Avertissement en caisse après (jours)</label>
                        <input id={`${uid}-warn`} value={f.offlineWarnDays} disabled={!f.offlineRule} onChange={(e) => set({ offlineWarnDays: e.target.value.replace(/\D/g, '') })} className="field-input" inputMode="numeric" />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label htmlFor={`${uid}-lock`} style={lbl}>Blocage automatique après (jours)</label>
                        <input id={`${uid}-lock`} value={f.offlineLockDays} disabled={!f.offlineRule} onChange={(e) => set({ offlineLockDays: e.target.value.replace(/\D/g, '') })} className="field-input" inputMode="numeric" />
                      </div>
                    </div>
                    <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink500)', marginTop: 10, lineHeight: 1.5 }}>
                      {f.offlineRule
                        ? 'Les jours se comptent sans aucune synchronisation réussie, caisse par caisse. Le blocage vient toujours après l’avertissement ; une synchronisation réussie débloque la caisse seule.'
                        : 'Désactivée : une caisse peut rester sans synchronisation indéfiniment — elle échappe alors à la consolidation fiscale et au kill-switch.'}
                    </div>
                  </>
                )}

                {cur === 'caisse' && (() => {
                  const estDepot = f.stKind === 'depot';
                  return (
                    <>
                      <div style={{ ...eyebrow, marginBottom: 10 }}>première caisse (option)</div>
                      {/* ⚠️ Un DÉPÔT n'encaisse pas : ni caisse, ni compteur fiscal, ni clôture. Proposer
                          le choix ici laisserait croire l'inverse — on l'explique au lieu de le griser. */}
                      {estDepot ? (
                        <EmptyState compact icon="cash" title="Un dépôt n'a pas de caisse"
                          note="Il stocke et expédie sans vendre : ni encaissement, ni compteur fiscal, ni clôture. Cette étape ne s'applique pas." />
                      ) : osErr ? (
                        <div className="err">Parc non chargé : {osErr} — impossible de savoir quelles caisses sont disponibles.</div>
                      ) : (
                        <>
                          <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink500)', lineHeight: 1.55, marginBottom: 14 }}>
                            Affecter la caisse ici évite d'y revenir : elle reçoit son numéro <b>R01</b> et son <b>QR de mise en service</b>
                            {' '}dans la même exécution. Les caisses suivantes s'ajoutent depuis la fiche du magasin.
                          </div>
                          {/* Le sélecteur PARTAGÉ avec l'assistant d'équipement (`device-picker.tsx`) : la recherche porte aussi
                              sur le nom donné au parc. */}
                          <DevicePicker
                            devices={caissePool} selected={caissePick ? [caissePick] : []}
                            onToggle={(id) => setCaissePick(caissePick === id ? null : id)}
                            title="Caisse à affecter" icon="cash"
                            emptyPoolNote="Aucune caisse disponible au parc. Ajoutez-en une dans « Appareils », ou affectez-la plus tard depuis la fiche du magasin — la création n'en dépend pas."
                            selectedNote={() => `retenue · caisse n° 1 en ${regimeText(caisseRegime, caisseDates)}`}
                            footer={<>
                              {caissePick && <RegimeRow regime={caisseRegime} onRegime={setCaisseRegime} dates={caisseDates} onDates={setCaisseDates} />}
                              <div style={{ padding: '10px 15px', borderTop: '1px solid var(--ink100)', fontSize: 'var(--t-meta)', color: 'var(--ink500)', lineHeight: 1.5 }}>
                                {caissePick
                                  ? 'Le QR sera émis à la fin de la mise en service, valable 24 h et consommé au premier scan. Il est régénérable à tout moment depuis la fiche du magasin.'
                                  : 'Sans sélection, le magasin ouvre sans caisse — vous pourrez en affecter une depuis sa fiche, avec son QR.'}
                              </div>
                            </>}
                          />
                          {caissePick && (
                            <div data-wizard-nepting style={{ marginTop: 16, maxWidth: 460 }}>
                              <div style={{ ...eyebrow, marginBottom: 6 }}>paiement par carte — Nepting (option)</div>
                              <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink500)', lineHeight: 1.55, marginBottom: 10 }}>
                                Le code commerçant que cette caisse enverra à l’application Nepting à l’ouverture de sa session de paiement.
                                Vide, elle s’appuie sur la configuration faite dans l’application Nepting du terminal. Il se pose ou se change
                                ensuite depuis la fiche du magasin, caisse par caisse ; le back-office du client le voit sans pouvoir le changer.
                              </div>
                              <NeptingTerminalFields code={caisseNepting.code} url={caisseNepting.url}
                                onCode={(code) => setCaisseNepting((v) => ({ ...v, code }))} onUrl={(url) => setCaisseNepting((v) => ({ ...v, url }))}
                                labelStyle={lbl} inputStyle={{ ...fieldSt, fontFamily: 'var(--font-mono)' }} />
                            </div>
                          )}
                        </>
                      )}
                      {/* Le groupe que suivront TOUTES les caisses de ce magasin, celle-ci comme les suivantes. */}
                      {!estDepot && installableFor('caisse').map((a) => (
                        <div key={a.slug} style={{ marginTop: 16, maxWidth: 460 }}>
                          <WizardUpdateGroup slug={a.slug} label={`Groupe de mise à jour des caisses — ${a.name}`}
                            value={f.updateGroups[a.slug]} onChange={(v) => set({ updateGroups: { ...f.updateGroups, [a.slug]: v } })} />
                        </div>
                      ))}
                    </>
                  );
                })()}

                {cur === 'mobile-partner' && (
                  <>
                    <div style={{ ...eyebrow, marginBottom: 10 }}>Mobile Partner (option)</div>
                    <span id={`${uid}-mp`} className="field-title" style={lbl}>Équiper ce magasin d'un Mobile Partner</span>
                    <div role="group" aria-labelledby={`${uid}-mp`} style={{ display: 'flex', gap: 6, maxWidth: 260 }}>{([[false, 'Non'], [true, 'Oui']] as const).map(([v, l]) => <button key={l} onClick={() => set({ mobilePartnerOn: v })} style={seg(f.mobilePartnerOn === v)}>{l}</button>)}</div>
                    {f.mobilePartnerOn && (
                      osErr
                        ? <div className="err" style={{ marginTop: 14 }}>Parc non chargé : {osErr} — impossible de savoir quels Mobile Partner sont disponibles.</div>
                        : <div style={{ marginTop: 16 }}>
                            {/* Le sélecteur PARTAGÉ (`device-picker.tsx`), à cases CARRÉES : un magasin peut ouvrir avec plusieurs
                                terminaux — l'inventaire se fait à deux. */}
                            <DevicePicker
                              devices={osPool} selected={osPick} multiple
                              onToggle={(id) => setOsPick((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]))}
                              title="Terminaux à affecter" icon="box"
                              emptyPoolNote="Aucun Mobile Partner disponible au parc. Ajoutez-en un dans « Appareils » pour pouvoir l'affecter ici."
                              selectedNote={() => `retenu · sera affecté à ce magasin en ${regimeText(osRegime, osDates)}`}
                              footer={<>
                                {osPick.length > 0 && <RegimeRow regime={osRegime} onRegime={setOsRegime} dates={osDates} onDates={setOsDates} />}
                                <div style={{ padding: '10px 15px', borderTop: '1px solid var(--ink100)', fontSize: 'var(--t-meta)', color: 'var(--ink500)', lineHeight: 1.5 }}>
                                  {osPick.length === 0
                                    ? 'Choisissez au moins un terminal, ou répondez « Non » : un magasin équipé sans terminal n’aurait rien à mettre en service.'
                                    : 'Magasin de travail par défaut : celui que vous créez — il n’y en a pas d’autre à ce stade.'}
                                </div>
                              </>}
                            />
                          </div>
                    )}
                    {f.mobilePartnerOn && installableFor('mobile-partner').map((a) => (
                      <div key={a.slug} style={{ marginTop: 16, maxWidth: 460 }}>
                        <WizardUpdateGroup slug={a.slug} label={`Groupe de mise à jour des terminaux — ${a.name}`}
                          value={f.updateGroups[a.slug]} onChange={(v) => set({ updateGroups: { ...f.updateGroups, [a.slug]: v } })} />
                      </div>
                    ))}
                  </>
                )}

                {cur === 'reglages' && (
                  <>
                    <div style={{ ...eyebrow, marginBottom: 10 }}>réglages du magasin</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 16px' }}>
                      <Field label="En-tête de ticket" wide><input className="field-input" value={f.ticketHeader} onChange={(e) => set({ ticketHeader: e.target.value })} placeholder="Maison Vireau — Colmar Centre" /></Field>
                      <Field label="Pied de ticket" wide><input className="field-input" value={f.ticketFooter} onChange={(e) => set({ ticketFooter: e.target.value })} placeholder="Merci de votre visite" /></Field>
                      <div style={{ gridColumn: '1 / -1' }}>
                        <CalendrierExploitation
                          timezone={f.timezone} openDays={f.openDays}
                          onChange={(v) => set(v)} />
                      </div>
                      <div style={{ gridColumn: '1 / -1', display: 'grid', gap: 12 }}>
                        <ValidityField
                          title="Validité des avoirs"
                          desc="Datée : un avoir émis garde la durée en vigueur le jour de son émission. Un raccourcissement ne rattrape jamais les titres en circulation."
                          value={f.voucher} onChange={(v) => set({ voucher: v })} makeText={TEXTE_AVOIR}
                        />
                        <ValidityField
                          title="Validité des chèques cadeaux"
                          desc="Même règle : la date d'expiration est calculée à l'émission et gravée sur le titre."
                          value={f.gift} onChange={(v) => set({ gift: v })} makeText={TEXTE_CADEAU}
                        />
                      </div>
                    </div>
                  </>
                )}

                {cur === 'fidelite' && <EtapeFidelite v={f.fid} onChange={(fid) => set({ fid })} />}


                {cur === 'recap' && (
                  <>
                    <div style={{ ...eyebrow, marginBottom: 10 }}>récapitulatif</div>
                    {gaps.length > 0 && (() => {
                      const n = gaps.reduce((a, g) => a + g.problems.length, 0);
                      return (
                        <div role="alert" data-recap-gaps style={{ display: 'flex', gap: 11, padding: '13px 15px', marginBottom: 14, borderRadius: 'var(--r-sm)', border: '1px solid color-mix(in srgb, var(--wa-fg) 35%, transparent)', background: 'var(--wa-bg)' }}>
                          <span aria-hidden="true" style={{ width: 18, height: 18, borderRadius: 5, background: 'color-mix(in srgb, var(--wa-fg) 22%, transparent)', color: 'var(--wa-fg)', display: 'grid', placeItems: 'center', fontSize: 'var(--t-meta)', fontWeight: 800, flexShrink: 0, marginTop: 1 }}>!</span>
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{ fontSize: 'var(--t-body)', fontWeight: 700, color: 'var(--wa-fg)' }}>
                              {n > 1 ? `${n} informations manquent` : '1 information manque'}{mode === 'client-multi' ? ' avant de créer le client' : ' avant la mise en service'}
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
                              {gaps.map((g) => (
                                <button key={g.st.k} type="button" onClick={() => goStep(g.i)} style={{ display: 'flex', alignItems: 'baseline', gap: '4px 10px', flexWrap: 'wrap', padding: 0, border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer', color: 'var(--wa-fg)' }}>
                                  <span style={{ fontSize: 'var(--t-note)', fontWeight: 700 }}>{g.i + 1}. {g.st.t}</span>
                                  <span style={{ fontSize: 'var(--t-note)' }}>{g.problems.join(' · ')}</span>
                                  <span style={{ fontSize: 'var(--t-note)', fontWeight: 700, whiteSpace: 'nowrap' }}>Compléter →</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                    <div style={{ border: '1px solid var(--ink200)', borderRadius: 'var(--r-md)', overflow: 'hidden' }}>
                      {([['Client', mode === 'store' ? clientName : f.name || '—'], ...(mode === 'store' ? [] : [['Régime de TVA', f.vatFranchise ? `franchise en base (art. 293 B du CGI) — les caisses factureront sans TVA${f.vatNumberAssigned ? '' : ', aucun numéro de TVA'}` : 'assujetti à la TVA']]), ['Type de commerce', f.businessKind || '—'], ...(mode !== 'client-multi' ? [['Magasin', f.stName || '—'], ['GLN', f.stGln || '— à saisir'], ['Type', f.stKind === 'depot' ? 'Dépôt' : 'Point de vente'],
                        // ⚠️ LA BASE PILOT N'EST PAS CONSTRUITE (SA-2.9) : le récapitulatif dit ce qui se passe, pas ce que la
                        //    maquette promet (« créée avec ce magasin »).
                        ['Base Pilot', mode === 'client-mono' ? 'aucune — un seul magasin' : 'aucune — la consolidation de plusieurs magasins (Sliderz Pilot) n’est pas encore construite'],
                        ['Synchronisation', SYNC_MODES.find((m) => m.k === f.syncMode)?.label ?? '—'],
                        ['Synchro périodique', f.offlineRule ? `avertissement après ${f.offlineWarnDays} j sans synchronisation · blocage après ${f.offlineLockDays} j` : 'désactivée (profil itinérant)'],
                        // Ligne de la maquette : le récap NOMME les terminaux retenus. Dire « Oui » sans
                        // les lister laisserait valider une affectation sans savoir laquelle.
                        // ⚠️ La caisse figure au récapitulatif : c'est la SEULE ligne qui engage une
                        // numérotation fiscale et l'émission d'un jeton — la passer sous silence
                        // laisserait découvrir un QR qu'on n'a pas demandé.
                        ['Première caisse', f.stKind === 'depot' ? 'sans objet — un dépôt n’encaisse pas'
                          : !caissePick ? 'aucune — à affecter depuis la fiche du magasin'
                            : `${pickedName(caissePool, caissePick)} · n° R01 · ${regimeText(caisseRegime, caisseDates)} · QR émis en fin de mise en service`],
                        // Le code commerçant part avec la caisse : dit au récapitulatif, il ne se découvre pas sur la fiche.
                        ['Paiement par carte', f.stKind === 'depot' ? 'sans objet — un dépôt n’encaisse pas'
                          : !caissePick ? 'code Nepting à poser depuis la fiche du magasin, caisse par caisse'
                            : caisseNepting.code.trim()
                              ? `code commerçant Nepting ${normalizeMerchantCode(caisseNepting.code)} pour la caisse R01${caisseNepting.url.trim() ? ` · serveur ${caisseNepting.url.trim()}` : ''}`
                              : 'aucun code Nepting — la caisse s’appuiera sur la configuration de l’application Nepting du terminal'],
                        ['Mobile Partner', !f.mobilePartnerOn ? 'non équipé' : osPick.length === 0 ? '— terminal à choisir' : `${osPick.map((id) => pickedName(osPool, id)).join(' · ')} · ${regimeText(osRegime, osDates)}`],
                        // Le flux que les appareils suivront dès leur premier scan — dit, puisqu'il ne se voit nulle part ailleurs avant.
                        ['Mises à jour', [
                          ...(f.stKind !== 'depot' ? installableFor('caisse') : []).map((a) => `${a.name} : ${f.updateGroups[a.slug]?.name ?? 'flux public'}`),
                          ...(f.mobilePartnerOn ? installableFor('mobile-partner') : []).map((a) => `${a.name} : ${f.updateGroups[a.slug]?.name ?? 'flux public'}`),
                        ].join(' · ') || 'sans objet'],
                        // Le récap résume le programme par ses trois décisions structurantes — celles
                        // qu'on ne peut pas changer sans conséquence une fois des gains accumulés.
                        ['Fidélité', !f.fid.enabled ? 'aucun programme' : [f.fid.mode === 'cashback' ? 'cashback' : "bon d'achat", f.fid.tiersOn ? 'paliers actifs' : `conversion ${f.fid.conversion} %`, `bonus ${f.fid.welcomeBonus} €`].join(' · ')]] : [['Topologie', 'Multi-magasin — la base Pilot n’est pas encore construite'], ['Magasins', 'aucun pour l’instant — chacun se crée ensuite depuis la fiche du client']])] as [string, string][]).map(([k, v], i) => (
                        <div key={k} style={{ display: 'flex', gap: 14, padding: '11px 15px', borderTop: i ? '1px solid var(--ink100)' : 0 }}>
                          <span style={{ fontSize: 'var(--t-note)', color: 'var(--ink500)', width: 170, flexShrink: 0 }}>{k}</span><span style={{ fontSize: 'var(--t-lead)', fontWeight: 600 }}>{v}</span>
                        </div>
                      ))}
                    </div>
                    <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink500)', marginTop: 12 }}>La numérotation (S…, radical EAN) et la base sont attribuées et créées à l'étape suivante, puis le jeton d'appairage de la première caisse, si vous en affectez une ; le compte administrateur se crée en fin de parcours.</div>
                  </>
                )}
              </div>
            </div>
          )}

          {phase === 'run' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 300px', gap: 16, alignItems: 'start' }}>
              <div style={{ ...card, padding: 20 }}>
                <div style={{ fontSize: 'var(--t-title)', fontWeight: 700, marginBottom: 12 }}>{done ? '✓ Mise en service terminée' : runError ? 'Mise en service interrompue' : 'Mise en service en cours…'}</div>
                {runSteps.map((s, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 11, padding: '10px 0', borderBottom: '1px solid var(--ink100)' }}>
                    <span style={{ width: 22, height: 22, borderRadius: 'var(--r-xs)', display: 'grid', placeItems: 'center', flexShrink: 0, fontSize: 'var(--t-dense)', background: s.status === 'ok' ? 'var(--ok-bg)' : s.status === 'error' ? 'var(--da-bg)' : s.status === 'run' ? 'var(--in-bg)' : 'var(--ink100)', color: s.status === 'ok' ? 'var(--ok-fg)' : s.status === 'error' ? 'var(--da-fg)' : s.status === 'run' ? 'var(--in-fg)' : 'var(--ink400)' }}>{s.status === 'ok' ? '✓' : s.status === 'error' ? '✕' : s.status === 'run' ? '◍' : '·'}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
                        <span style={{ flex: 1, fontSize: 'var(--t-lead)', color: 'var(--ink700)' }}>{s.label}</span>
                        {s.detail && <span className="mono" style={{ fontSize: 'var(--t-meta)', color: 'var(--ink500)' }}>{s.detail}</span>}
                      </div>
                      {/* Le rapport du serveur, fait par fait — c'est ce qui manquait : une seule cellule
                          de détail ne pouvait porter que le premier des cinq. */}
                      {s.lines?.map((l) => (
                        <div key={l} className="mono" style={{ fontSize: 'var(--t-meta)', color: 'var(--ink500)', marginTop: 3 }}>{l}</div>
                      ))}
                      {/* ⚠️ AVERTISSEMENT et non erreur : le magasin EST créé, mais quelque chose reste à
                          faire. Le vert seul aurait laissé partir avec un magasin injoignable des caisses. */}
                      {s.warn?.map((w) => (
                        <div key={w} style={{ fontSize: 'var(--t-note)', color: 'var(--wa-fg)', marginTop: 4, lineHeight: 1.5 }}>⚠️ {w}</div>
                      ))}
                    </div>
                  </div>
                ))}
                {runError && <div className="err" style={{ marginTop: 12 }}>Arrêt : {runError} — rien de plus n'a été enregistré. Corrigez et relancez.</div>}
                {/* Écart entre le numéro annoncé à la saisie et celui réellement attribué. En AVERTISSEMENT
                    et non en erreur : le magasin est bien créé, correctement — c'est la lecture de
                    l'opérateur qui est périmée, et elle doit être rattrapée avant qu'il note le mauvais. */}
                {ecartCompteur && (
                  <div style={{ marginTop: 12, padding: '10px 12px', borderRadius: 'var(--r-sm)', background: 'var(--wa-bg)', border: '1px solid var(--wa-fg)', color: 'var(--wa-fg)', fontSize: 'var(--t-note)', lineHeight: 1.5 }}>
                    ⚠️ {ecartCompteur}
                  </div>
                )}
              </div>
              <div style={{ ...card, padding: 18 }}>
                <div style={{ fontSize: 'var(--t-lead)', fontWeight: 700, marginBottom: 8 }}>Ce que ça garantit</div>
                <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink500)', lineHeight: 1.55 }}>Base propre (schéma + gardes append-only NF525), numérotation figée dès la 1ʳᵉ vente, DSN enregistré à l'annuaire. Le rôle applicatif du magasin ne peut pas désactiver les triggers fiscaux.</div>
                {/* Suite NATURELLE du parcours, une fois la base debout (maquette : fin de parcours).
                    ⚠️ On ne montre le QR que s'il y a une CAISSE à mettre en service : le jeton s'émet
                    par appareil, pas par magasin — sans appareil affecté, il n'y a rien à appairer. */}
                {done && mode === 'client-multi' && (
                  <button className="btn btn-cta sm" style={{ width: '100%', marginTop: 12 }}
                    onClick={() => onDone(createdClientId ?? undefined)}>
                    Créer le 1ᵉʳ magasin →
                  </button>
                )}
                {/* QR affiché SEULEMENT si une caisse a réellement été affectée : le jeton porte un
                    appareil, pas un magasin. ⚠️ Son ÉCHÉANCE est écrite en clair — c'est ce qui rend
                    l'émission anticipée honnête : sans elle, on repartirait avec une clé périmée sans
                    le savoir. Elle se régénère d'un clic depuis la fiche du magasin. */}
                {done && caisseJeton && (
                  <div style={{ marginTop: 12, padding: 14, borderRadius: 'var(--r-sm)', border: '1px solid var(--wa-fg)', background: 'var(--wa-bg)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
                      <span style={{ fontSize: 'var(--t-body)', fontWeight: 700 }}>
                        QR de mise en service{caisseJeton.registerNumber != null ? ` — caisse n° ${caisseJeton.registerNumber}` : ''}
                      </span>
                    </div>
                    {/* ⚠️ Blanc EN DUR : un QR se décode par contraste, sa marge doit rester blanche
                        même en thème sombre — sinon l'appareil ne lit plus le code. */}
                    <div style={{ background: '#fff', padding: 10, borderRadius: 'var(--r-xs)', width: 132, lineHeight: 0 }} dangerouslySetInnerHTML={{ __html: qrSvg(caisseJeton.url) }} />
                    <div className="mono" style={{ fontSize: 'var(--t-sub)', overflowWrap: 'anywhere', textAlign: 'center' }}>{caisseJeton.token.slice(0, 18)}…</div>
                    <div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink600)', textAlign: 'center', lineHeight: 1.5 }}>
                      Usage unique · <b>expire le {dateTimeLong(caisseJeton.expiresAt)}</b>. Passé ce délai, régénérez-le
                      depuis la fiche du magasin — la caisse et son numéro restent en place.
                    </div>
                  </div>
                )}
                {done && mode !== 'client-multi' && !caisseJeton && (
                  <div style={{ marginTop: 12, padding: '11px 13px', borderRadius: 'var(--r-sm)', background: 'var(--in-bg)', color: 'var(--in-fg)', fontSize: 'var(--t-note)', lineHeight: 1.55 }}>
                    Prochaine étape : <b>affecter une caisse</b> à ce magasin depuis sa fiche, puis
                    « Générer le QR » pour sa mise en service. Le jeton s'émet <b>par appareil</b>,
                    il n'existe donc pas encore.
                  </div>
                )}
                {done && <AdminAccountOffer openedStore={openedStore} contactEmail={f.contactEmail} />}
              </div>
            </div>
          )}
        </div>

        {/* FOOTER */}
        <div style={{ padding: '14px 24px', borderTop: '1px solid var(--ink200)', display: 'flex', alignItems: 'center', gap: 10 }}>
          {err && <div className="err" style={{ margin: 0, flex: 1 }}>{err}</div>}
          <div style={{ flex: err ? 0 : 1 }} />
          {phase === 'steps' && <button className="ghost btn-ghost" style={{ width: 'auto', padding: '9px 16px' }} onClick={back}>← Retour</button>}
          {phase === 'steps' && (
            <button className="btn btn-cta sm" onClick={next} disabled={launchLocked}
              style={launchLocked ? { background: 'var(--ink200)', color: 'var(--ink400)', boxShadow: 'none', cursor: 'not-allowed' } : undefined}>
              {launchLocked ? 'Étapes à compléter' : step === stepList.length - 1 ? (mode === 'client-multi' ? 'Créer le client' : 'Lancer la mise en service') : 'Continuer →'}
            </button>
          )}
          {phase === 'type' && <button className="ghost btn-ghost" style={{ width: 'auto', padding: '9px 16px' }} onClick={() => { setPhase('entry'); setMode(null); }}>← Retour</button>}
          {phase === 'run' && (done || runError) && <button className="btn btn-cta sm" onClick={() => onDone(done ? (createdClientId ?? undefined) : undefined)}>{runError ? 'Fermer' : done && createdClientId ? 'Ouvrir la fiche client' : 'Terminer'}</button>}
        </div>
      </div>
    </div>
  );
}
